import { clamp, hash01, hash3, idSeed, mulberry32 } from '@/core/rng';
import { DAY } from '@/sim/clock';
import type { SimSystem } from '@/sim/scheduler';
import type { EventBus, EventRecord } from '@/sim/events';
import type { HabitatSampler, SocialHabitat } from '@/sim/social/habitat';
import { socialCenter, socialNeighbors, toSocial, SOCIAL_CELL_SIZE }
  from '@/sim/social/socialGrid';
import { DECISION_STEP, VISIT_INTERVAL, decide, placeValue, pressuresOf, scoreTable,
  type DecisionContext, type Pressures } from './decisions';
import { ARRIVE_RADIUS, HOUR, stepAlong, type MoveMode } from './movement';
import { MortalPressure, stampDailyImpact, type MortalPressureCell } from './impact';
import { ConstructionSystem, FUNCTIONAL, HEARTH_RANGE, SITE_RANGE } from './construction';
import { PATH_SAMPLE, PATH_SPEED, PathSystem } from './paths';
import {
  assignPeople, axesOf, bereavedBy, chooseParent, crossingsSince, fadeBonds, fadeHaunts,
  foundingBornAt, GRIEF, liveADay, makeChild, makePerson, mourn, press, rosterNorm, shareADay,
  workADay, type Crossing,
} from './people';
import { died } from './mortality';
import { omenSpecificity, omenUncertainty } from '@/sim/omen';
import { CultureSystem, type CultureState } from '@/sim/culture';
import { NOTICE_RANGE, NO_ANCHORS, type AnchorReader } from '@/sim/anchor';
import type {
  AnomalyMemory, BandActivity, BandState, OmenMeaning, PartyState, PathRecord,
  PersonMemory, PersonState, PlaceMemory, SacredMemory, StructureState, WitnessKind,
  WorkSite,
} from './types';
import type { BandSnapshot, BandSystemSnapshot } from '@/sim/persistence/snapshot';

/**
 * The band system (spec Task 4): mortal communities as aggregates — one
 * position, one activity, shared reserves. Bands read the same habitat the
 * fire fears and stamp the same EnvField the gods write, so repeated
 * occupation visibly wears the land and cures tomorrow's fuel.
 *
 * Determinism: advance() walks the absolute hour grid; hours divide the
 * six-hour decision grid and the daily grid, so advance(a,b)+advance(b,c)
 * runs the identical boundary sequence as advance(a,c). Every stochastic
 * choice hashes (band identity, absolute index, world seed) — no shared
 * streams, no wall clock, no Math.random. Same state, same time, same band.
 */

/** EnvField satisfies this — the one door through which mortals mark land. */
export interface LandWriter {
  stamp(
    wx: number,
    wz: number,
    radius: number,
    d: { moisture?: number; biomass?: number },
    at: number,
  ): string[];
  /** What the land remembers here, or null where nothing has happened. */
  at(wx: number, wz: number, now: number): { moisture: number; biomass: number } | null;
  /**
   * The best-remembered growth within `radius`, or null. Sparse: in a world
   * with no god in it nothing has ever written positive biomass, so this is
   * null everywhere and costs one empty iteration.
   */
  bestGrowth(wx: number, wz: number, radius: number, now: number):
    { x: number; z: number; biomass: number } | null;
}

/** Active danger at a point, 0..1. The integrator wires this to live fire. */
export interface Hazards {
  dangerAt(x: number, z: number, now: number): number;
}

/** The slice of WildlifeSystem bands consume. Head counts in, food out. */
export interface WildlifeReader {
  populationAt(sx: number, sz: number, species: string): number;
  harvest(sx: number, sz: number, species: string, effort01: number, now: number): number;
}

// water caps at two days — skins and gourds, not cisterns. The food cap
// lives with construction: two weeks bare, a month with a granary (spec §28).
const WATER_CAP_DAYS = 2;
/**
 * THE WORKING DAY (2026-08-23, Griffin: the mortal world should read as
 * alive — and a camp is most alive at dusk, when the day comes home).
 *
 * The band system had no concept of night: `tools/blocks.ts` measured the
 * four six-hour blocks of a day carrying near-identical person-hours — hunts
 * as hard at three in the morning as at noon — because decisions fire on
 * absolute boundaries and the tables never read the sun. Now the two dark
 * boundaries (18:00 and midnight — tod 0.52 is midday, so blocks 3 and 0
 * are night) fold the field home: forage, wood, frames, scouting and visits
 * end at dusk and their parties WALK home (never teleport — the glide and
 * the ways draw the return), everyone keeps the fires until the 06:00
 * boundary musters the morning's parties out again.
 *
 * Exceptions, each an honest one: HUNTS are expeditions and stay out (their
 * harvest settles on the six-hour boundary and the herd does not keep camp
 * hours); FLEEING keeps running; a MARCHING band keeps marching; and a band
 * hungry (< 1 day of food) or parched (< 0.8 days of water) works the dark,
 * because need does not keep hours either.
 *
 * The economy is re-priced, not halved: hourly yields divided by 24 assumed
 * a 24-hour working day, so forage and wood now divide by WORK_HOURS = 12
 * (the two daylight blocks), build passes 24/WORK_HOURS hours per worked
 * hour, and explore's uncertainty relief doubles. Spec §10's own number —
 * 0.9 person-days per person per FULL DAY at forage 1.0 — survives to the
 * digit; only the hours it happens in have moved. Hunts needed no
 * compensation: they never stopped.
 */
const WORK_HOURS = 12;
/** Dark six-hourly boundaries: midnight (opens 0–6 h) and 18:00 (opens 18–24). */
const darkBoundary = (decisionIndex: number): boolean =>
  decisionIndex % 4 === 0 || decisionIndex % 4 === 3;
/**
 * Field activities the dusk folds home: the NEAR work, done within a short
 * walk of camp. Everything else keeps its own law — and the far errands are
 * all journeys: a hunt camps by its quarry, a pilgrimage is 1.5 km each way
 * (a 1.2-day round trip at walking pace — folding it meant `test/theology`
 * counted 0 visits in 420 days), a scout's sweep and a sign's search reach
 * as far. Expeditions come home when the errand is done, as they always
 * did; the working day is for hands, not for feet.
 */
const FOLDS_AT_DUSK: ReadonlySet<BandActivity> = new Set<BandActivity>([
  'forage', 'gather_wood', 'build', 'seek_water',
]);
/** A party this far from camp with nothing left to do walks back. */
const HOME_RETURN = 260;
/** Salt for a band's people, so their names never echo its other hashes. */
const PERSON_SALT = 0x7ea1;
/**
 * Growth (the band must be able to become a village). Under good conditions a
 * band adds this fraction of itself per day, which doubles it in roughly four
 * world-years — slow enough that a village is an achievement, fast enough that
 * a player who tends a valley sees one. "Good" is deliberately demanding: a
 * full larder, roofs for everyone and a band that holds together. Shelter in
 * particular means construction finally has demographic consequences.
 */
const GROWTH_RATE = 0.00047;
/**
 * Days of food in hand before a band considers growing, and the span over
 * which plenty saturates. Measured, not guessed: over a two-year god-free run
 * a band's larder sits at a median of 3.5 person-days and has *never once*
 * exceeded 4.5. A first attempt asked for five days and therefore asked for
 * something the world has never produced — the same mistake that left the
 * settlement clearing switched off for a milestone. Two and a half days is
 * genuine surplus here; four is as good as this economy gets.
 */
const GROWTH_FOOD_DAYS = 2.5;
const GROWTH_FOOD_SPAN = 1.5;
/** movedToday at or past this reads as a day on the move, for pressure. */
const TRAVELLED_METRES = 200;
/** Above this waterAccess the band drinks where it stands. */
const NEAR_WATER = 0.45;
/** Fraction of the band lost per day at empty stores (spec §11). */
const STARVE_RATE = 0.02;
/** Fraction lost per waterless day past the second — thirst outruns hunger. */
const THIRST_RATE = 0.04;
const MAX_MEMORIES = 32;
const MAX_PLACES = 48;
/** How far beyond an event's own footprint it can still be witnessed. */
const WITNESS_RANGE = 1200;
/**
 * A sign is different, because being seen is the entire act.
 *
 * Everything else in the world is witnessed incidentally: a fire is noticed
 * because it is bright, a herd because it is large. An Omen exists only to
 * be perceived, so it carries — a portent over a distant hill is a thing
 * people look up at from the next valley. The first theology test found this
 * the hard way: with a thunderclap's reach, a sign cast over a place a
 * kilometre and a half away was seen by nobody at all, which made the
 * informational verb useless for the one job it is for — telling people
 * about somewhere they are not.
 *
 * Distance still blurs it. Seeing a sign from five kilometres tells you
 * almost nothing about where it was (see omenUncertainty), which is how a
 * far portent stays a rumour rather than becoming a map.
 */
/**
 * How far an Omen carries. Exported because `main.ts` reports the audience
 * back to the player and was inventing its own number — `focus.radius + 1200`
 * against this 6000 — so the one faculty that tells you who could have seen it
 * under-reported its own reach by 4.8 km.
 */
export const OMEN_RANGE = 6000;
const SALIENT = 0.35;

/**
 * Work that is done AT the fire. A party on one of these is not out on the
 * ground and is not approached — see `predatorsMeet`. The same set
 * `people.ts` treats as asking nothing of anybody, for the same reason: it is
 * the list of ways to be at camp.
 */
const AT_CAMP: ReadonlySet<BandActivity> = new Set<BandActivity>(['camp', 'idle', 'rest']);
/**
 * What one person is worth against one predator when it comes to being
 * approached at all. **STATUS: GUESSED**, and it is the only un-measured
 * number in `predatorsMeet` — the probe is `tools/tempo.ts`, which already
 * reports this event type at zero and will report the new rate. At 3 a party
 * of four is even money against a pack of twelve, which is the shape the
 * archetype's `headAtBest: 8` makes plausible.
 */
const PARTY_SAFETY = 3;
/**
 * How much of a fully-exposed six hours becomes an incident.
 *
 * **MEASURED, `tools/predators.ts`, and the first guess was wrong by 43×.**
 * At 0.06 the world produced **14.17 incidents per band per world-year** — one
 * every 26 days, ~567 over a 40-year life — which is not a hazard, it is a
 * climate. A band that meets something that hunts twice a month does not
 * remember it, and the whole value of this event is that it is *carried*.
 *
 * The target is stated rather than felt for, because a frequency has to be
 * argued: **one per band every three world-years or so.** Rare enough that it
 * is news and the memory survives eviction; common enough that a band living
 * in predator country genuinely carries one, and that a god crossing an island
 * of sixteen bands meets one now and then. Over a life that is ~13 — present
 * in a person's history without crowding out the rest of it.
 *
 * 0.06 → 0.0014 is that 43×. Re-measure after any change to `PARTY_SAFETY`,
 * to the predator archetype, or to how parties are placed.
 */
const PREDATOR_RATE = 0.0014;
/** How far the fright carries. Under `WITNESS_RANGE`, so the party it
 *  happened to remembers it directly and the camp hears about it. */
const PREDATOR_REACH = 120;
/** At or above this salience, a witnessed event unsettles the witnesses. */
const UNSETTLING = 0.6;
/**
 * The witness grades (v0.3.2): under it, in sight of it, in earshot of it.
 * A party at an event's rim is *in* it — members scatter tens of metres —
 * hence the margin on direct. Beyond earshot there is no grade: a person
 * not yet informed holds nothing at all.
 */
const DIRECT_MARGIN = 40;
const OBSERVED_RANGE = 520;
const KIND_WEIGHT = { direct: 1, observed: 0.62, heard: 0.3 } as const;
/** What one person can carry. The faintest memory is the first forgotten. */
const MAX_PERSON_MEMORIES = 10;
/** Unexplained signs a band keeps on its books at once. */
const MAX_ANOMALIES = 6;
/** Close enough to the believed point to say you have looked. */
const SEARCHED = 140;
/**
 * How far a party that came here *looking* will range before it gives up.
 *
 * Incidental discovery is NOTICE_RANGE — what you happen to walk past. This
 * is much wider, because searching is not walking: these people crossed a
 * kilometre and a half on the strength of something they saw in the sky, and
 * they are going to comb the hillside before they go home. Measured, without
 * this: a party arrived 253 m from the standing stone, found ordinary ground,
 * wrote the sign off as empty, and only came across the stone months later by
 * accident — which makes the informational verb unable to deliver anyone to
 * the thing it was about.
 */
const SEARCH_RANGE = 420;
/**
 * How much a fully blessed field multiplies a day's gathering. At the +0.84
 * biomass a sustained blessing actually reaches, this takes a forager from
 * 0.8 person-days to about 1.9 — from never quite feeding themselves to
 * feeding themselves and one other. Tuned against tools/strengthen.ts, which is
 * the instrument for changing it.
 */
const BOUNTY_GAIN = 1.3;
/**
 * How far from home a party will walk to work ground the land remembers well.
 * One social cell and a half — the same country `socialNeighbors` already
 * reaches, so tended ground is considered on the same terms as habitat and
 * never further.
 */
const TENDED_REACH = SOCIAL_CELL_SIZE * 1.5;
/**
 * Cohesion's daily pull toward what conditions deserve, and the conditions
 * it reads. A fortnight to track, so a bad month shows and a bad week does
 * not; five days of food is a community that can afford to be generous with
 * each other; thirty is a size that is still one group and seventy is not;
 * and a kilometre is a long way for your people to be from you all day.
 */
const COHESION_TRACK = 0.07;
const COHESION_FED_DAYS = 5;
const COHESION_EASY_SIZE = 30;
const COHESION_SIZE_SPAN = 40;
const COHESION_SPREAD_METRES = 1000;
/**
 * Fission (§48). A community splits when it has been coming apart for a
 * season rather than a bad week, and only when both halves would be a
 * community rather than a stranded handful. The share that leaves is the
 * smaller one — leaving is the harder choice — and they go far enough to be
 * their own people rather than a second camp in the same valley.
 */
/**
 * How fast a band's own character follows the character of its roster, per
 * day. **STATUS: MEASURED**, `tools/press.ts` §3 — see there for the readings
 * this was chosen against and the two it must not break.
 *
 * A move-toward, so the time constant is `1 / BAND_TRACK` days: at 0.002 a
 * band is ~500 days (1.4 world-years) behind its people. That lag is the
 * feature. A band is its history as well as its roster, and a rate fast enough
 * to make the band equal its people would delete the difference between the
 * two — which is the thing fission exists to express.
 */
const BAND_TRACK = 0.002;

const FISSION_COHESION = 0.36;
const FISSION_DAYS = 45;
const FISSION_MIN_POP = 34;
const FISSION_MIN_HALF = 12;
const FISSION_SHARE = 0.4;
const FISSION_DISTANCE = 4200;
/** How much of the old country the departing band stops counting as theirs. */
const FISSION_DISOWN = 1400;
/**
 * Fusion (§48), the other half. Two communities that keep finding themselves
 * in the same country come to know each other, and a small one that cannot
 * hold on its own joins the neighbour it knows.
 *
 * Every number here exists to stop the two engines fighting. Merging must
 * produce a band comfortably below the size that fission takes apart, or the
 * world would oscillate — join in spring, split in autumn, forever. And
 * knowing each other has to be *earned*: contact is counted in days and
 * decays when they drift apart, the same way a path is earned by walking it
 * and lost by not.
 */
const CONTACT_RANGE = 2600;
const CONTACT_DECAY = 2;
const FUSION_DAYS = 60;
/**
 * A STORY CROSSES BETWEEN BANDS — link 5 of `PLAYABLE.md`'s chain, the one
 * break that was left: `deliverNews` carried a memory home to its own hearth
 * and nothing carried it any further, so a consequence could not leave the
 * community it happened in.
 *
 * **Measured before it was written** (`tools/carry.ts`, the shipped century,
 * two world-years in six-hour slices): parties of different bands stand
 * within 260 m of each other ~1,930 times per band-year, 27 band pairs do it,
 * 15 of 16 bands meet somebody, and a pair that meets does so every 5.3 days
 * at the median. The island's camps stand in seven CLUSTERS on shared ground
 * (twelve of sixteen within 60 m of another camp), so a story that crosses
 * at meetings reaches four or five communities within the first half-year
 * and never crosses the 3.7–13 km between clusters. That is the reach this
 * rule has on this island, and it was the reach before the rule existed —
 * only now something travels it.
 *
 * The radius is `HOME_RETURN`, the system's own "here": the distance inside
 * which a party counts as at the hearth for `deliverNews`. The encounter
 * count is flat from 130 m to 260 m (61,518 against 61,704), so the number
 * barely matters and is not tuned; reusing the hearth radius means a visitor
 * standing where the hearth would hear them is standing where strangers do.
 *
 * **Second-hand is second-hand wherever it is heard.** A story crosses at
 * `SECOND_HAND` of what the teller holds — the same discount `deliverNews`
 * gives the hearth — and then walks home in the listener's head exactly as
 * any party's news does, so the receiving band's communal record takes it at
 * `SECOND_HAND` and its hearth-sitters at `SECOND_HAND²`. Nothing new is
 * stored for it and no second pipeline exists: one field on the memory says
 * whose story it was.
 *
 * **Bounded by construction.** One story per meeting per direction — the
 * strongest thing the telling party holds that the listening side does not,
 * where "strongest" is how hard the memory is still held × how readily its
 * holder's account spreads (`sociability`, which this is the first reader
 * of: the trait's own definition is *how readily their account of a thing
 * spreads to others*). A band that already holds the story communally does
 * not hear it again; a listener's head keeps its cap and its eviction, so a
 * faint rumour cannot displace what they saw themselves. Nobody inherits a
 * whole head.
 */
const MEET_RANGE = HOME_RETURN;
/** What a retelling is worth against the witness's own memory. */
const SECOND_HAND = 0.45;
/** Merged size must stay well under FISSION_MIN_POP or the two engines cycle. */
const FUSION_MAX_COMBINED = 31;
/** One of them has to actually need it: a community too small to hold. */
const FUSION_WEAK = 15;
/**
 * And they have to still be recognisably the same kind of people. Two bands
 * that parted generations ago and drifted apart do not simply re-merge —
 * that is what keeps §50's divergence from quietly undoing itself.
 */
const FUSION_KINSHIP = 0.55;
/**
 * A generation apart from your own people and you are not their people any
 * more (§50). Twenty world years: long enough that a band splitting off and
 * "remaining part of the same culture for generations" is literally true,
 * short enough that a century of history produces several peoples.
 */
const CULTURE_APART_DAYS = 20 * 365;
/**
 * What a day regathered erodes from apartness: a season together unwinds a
 * year apart, so drift survives a census flap and dies to genuine reunion
 * (2026-08-23 — see the accumulation site for the trap this replaces).
 */
const CULTURE_REGATHER = 4;
/**
 * How far from your own people's centre of gravity you can live and still be
 * one of them. Measured (tools/) on a grown island at sixty years: bands sit
 * a median of 3.5 km from their people's centre, the tail reaches 11.5 km and
 * the widest pair are 17.6 km apart. Six kilometres therefore holds the body
 * of a people together in one valley system while putting its outer quarter
 * beyond reach — which is the outer quarter that should, given a generation,
 * stop being the same people.
 */
const CULTURE_REACH = 6000;
const DAY_SECONDS = 86400;
/**
 * What demands explanation is what sticks. Measured without this, a person's
 * ten slots filled with herd movements (salience up to 0.85 — they are big)
 * and a heard miracle (0.49 × 0.3) was evicted in the very tick it formed:
 * the divine lost to the routine everywhere, communally too, because the
 * hearth's record shares the same eviction. Salience measures how *large*
 * a thing was; memorability is how strange — so the unexplained outweighs
 * the everyday, and grim news cuts deeper than good.
 */
const memorability = (e: EventRecord): number =>
  e.cause.kind === 'divine' ? 1.5 : GRIM.has(e.type) ? 1.15 : 1;

/**
 * Is this worth remembering at all?
 *
 * Measured on day 40 of a god-free world: every head was ten out of ten herd
 * movements, and so was the band's whole ledger — a miracle formed and was
 * evicted in the same tick, not because it meant less but because herds are
 * the loudest, most frequent thing in the world. The Chronicle had already
 * ruled on this: it refuses to speak a herd movement aloud unless it was a
 * great migration, because ordinary herds are weather. Memory must agree
 * with the world's own voice, or a band's history is a weather log with no
 * room for anything that happened.
 */
const GREAT_MIGRATION = 0.7;
const worthRemembering = (e: EventRecord): boolean =>
  e.salience >= SALIENT &&
  (e.type !== 'wildlife_migrated' || e.salience >= GREAT_MIGRATION);

/**
 * Memory fades. Weighting alone was not enough: with a full head, eviction
 * by raw importance let a two-month-old herd movement throw out a miracle
 * in the tick it formed, because it happened to have been a larger event.
 * What a memory is *worth keeping* is what it meant, faded by how long ago
 * — so the recent survives the routine, and only the genuinely strange
 * survives the years. The stored importance never changes; this shades the
 * eviction only, so what the panel reads is still how the thing landed.
 */
const MEMORY_HALFLIFE = 90 * 86400;
const hold = (importance: number, time: number, now: number): number =>
  importance * Math.pow(0.5, Math.max(0, now - time) / MEMORY_HALFLIFE);
/** Events that read as wounds rather than wonders. */
/**
 * The events a band remembers as wounds — 1.15× as memorable, and stored with
 * the valence that says so.
 *
 * **`person_died` was missing, and its absence was a real mis-statement about
 * the world.** A death was weighted as ordinary news and written at POSITIVE
 * valence, so as far as the band's own memory was concerned somebody dying was
 * the same kind of day as a herd moving through. It is latent rather than
 * active — nothing reads `valence` yet — but it is wrong in the store, and a
 * store that is wrong before it has readers is worse than one that is empty:
 * the first reader inherits the error and nobody looks again.
 *
 * It matters for `PLAYABLE.md` directly. *When this person dies or leaves, I
 * should understand that something was lost* is the whole test for
 * relationships, and it cannot be met by a memory system that files the death
 * of somebody you worked beside for ten years under news.
 */
const GRIM = new Set(['fire_started', 'fire_burned', 'predator_incident',
                      'resource_shortage', 'person_died']);

/** BandState plus bookkeeping the outside world never needs to read. */
/**
 * A party plus the bookkeeping nobody outside needs to read. Each party keeps
 * its own walk: its own target, its own sampled trace, its own metres walked.
 * That is what gives a settlement an activity network rather than one trail —
 * the water party wears the river path, the hunters wear the ridge path.
 */
interface Party extends PartyState {
  /** Identity of the walk under way ("x,z" of its target); '' when still. */
  walkKey: string;
  /** The walk's actual polyline so far, sampled every ~PATH_SAMPLE metres. */
  trace: Array<{ x: number; z: number }>;
  /** Metres walked since the last day boundary — a still day is a camp. */
  movedToday: number;
  foragedToday: boolean;
  gatheredToday: boolean;
}

interface Band extends BandState {
  idHash: number;
  /** The internal view of the parties BandState exposes. */
  parties: Party[];
  /** Parties founded so far, for stable party ids. */
  nextParty: number;
  /** The current travel is a migration; arrival should say so. */
  migrating: boolean;
  /** A shortage event was already emitted for the ongoing episode. */
  shortageOpen: boolean;
}

/**
 * The runtime shapes of the construction, path and pressure internals, for
 * persistence only. TypeScript privacy is compile-time; the fields exist at
 * runtime, and a save that lost the site counters or traversal counts would
 * resume as a different world. Persistence is the one reader allowed behind
 * these doors — nothing else may import or imitate these shapes.
 */
interface ConstructionInternals {
  structureList: StructureState[];
  siteList: WorkSite[];
  counters: Map<string, { settledDays: number; fullDays: number }>;
  nextId: number;
}

interface PathInternals {
  list: PathRecord[];
  pairs: Map<string, { count: number; pathId?: string }>;
  versionCounter: number;
  nextId: number;
}

interface PressureInternals {
  cells: Map<string, MortalPressureCell>;
}

/** A band, deep-copied — both directions of the snapshot trip use this. */
const cloneBand = (b: BandSnapshot): BandSnapshot => ({
  ...b,
  // saves from before parties existed restore as one camp party of everyone
  parties: (b.parties ?? [{
    id: `${b.id}-p0`, activity: 'camp', size: b.population, x: b.x, z: b.z,
    walkKey: '', trace: [], movedToday: 0,
    foragedToday: false, gatheredToday: false,
  }]).map((p) => ({ ...p, ...(p.target ? { target: { ...p.target } } : {}),
                    trace: p.trace.map((q) => ({ x: q.x, z: q.z })) })),
  nextParty: b.nextParty ?? 1,
  // a save from before people existed keeps its number and grows faces back;
  // one from before witnesses were personal restores with unwitnessed people
  // `shown` is spelled out for the same reason `traits` and `memories` are:
  // a spread copies an object BY REFERENCE, so a restored world would share
  // one expression counter with its donor and every day either of them lived
  // would be lived by both. That corruption is silent — the copy looks right
  // until you mutate the donor — and `test/lives.ts` checks it by doing
  // exactly that. A save from before a life could shape anybody restores with
  // nobody shaped, which is the truth about that world.
  // `bonds` is spelled out for exactly the reason the paragraph above gives,
  // and it is an ARRAY, which is the sharper half of the same trap: a spread
  // shares it by reference, so a restored world and its donor would keep one
  // list of companions between them and every day either lived would be
  // recorded in both. A save from before company was remembered restores with
  // nobody keeping company, which is the truth about that world.
  people: (b.people ?? []).map((p) => ({
    ...p, traits: { ...p.traits }, aptitudes: { ...p.aptitudes },
    memories: (p.memories ?? []).map((m) => ({ ...m })),
    ...(p.shown ? { shown: { ...p.shown } } : {}),
    ...(p.bonds ? { bonds: p.bonds.map((x) => ({ ...x })) } : {}),
    ...(p.lost ? { lost: p.lost.map((x) => ({ ...x })) } : {}),
    ...(p.became ? { became: p.became.map((x) => ({ ...x })) } : {}),
    ...(p.born ? { born: { ...p.born } } : {}),
    ...(p.haunts ? { haunts: p.haunts.map((x) => ({ ...x })) } : {}),
  })),
  nextPerson: b.nextPerson ?? (b.people?.length ?? 0),
  // a save from before signs existed has simply never been shown one
  anomalies: (b.anomalies ?? []).map((a) => ({ ...a })),
  sacred: (b.sacred ?? []).map((s) => ({ ...s })),
  // a save from before bands could come apart has never been fraying
  frayedDays: b.frayedDays ?? 0,
  cultureApart: b.cultureApart ?? 0,
  omenTrust: b.omenTrust ?? 0.6,
  // saves from before thirst was dangerous simply haven't been thirsty yet
  waterlessDays: b.waterlessDays ?? 0,
  reserves: { ...b.reserves },
  traits: { ...b.traits },
  ...(b.target ? { target: { ...b.target } } : {}),
  knownPlaces: b.knownPlaces.map((p) => ({ ...p })),
  memories: b.memories.map((m) => ({ ...m })),
});

/**
 * WHY A BAND IS DOING WHAT IT IS DOING, as a thing a card can read.
 *
 * `BandSystem.explain` has returned the scored table since the omen work and
 * `tools/omen.ts` is its only caller — the single best *understand it* payload
 * in the build, and the player has never been able to reach it. This is that
 * payload, plus the four things a table of scores cannot say on its own.
 *
 * **Nothing here is player-facing text and nothing here is a UI decision.** It
 * is simulation truth in simulation units; `ui/thread.ts` is what turns it
 * into a sentence, and the wall between them is the same one `ui/lives.ts`
 * keeps: the sim may say `food: 0.72`, the screen may only say *hungry*.
 */
export interface BandReading {
  /** What they are ACTUALLY doing. Not always the table's maximum — see `override`. */
  chose: BandActivity;
  /**
   * Set when the decision never reached the table at all. `decide` jumps
   * straight past it for fire at the door and for a remembered place worth
   * leaving home for, which are the two moments a card most wants to explain.
   */
  override?: 'fire' | 'migrating';
  /** Every row, scored, in the order `scoreTable` builds them. */
  table: ReadonlyArray<{ a: BandActivity; s: number }>;
  /** The table's own winner. Equal to `chose` unless `override` is set. */
  best: BandActivity;
  /** What they nearly did instead, when there was a real second choice. */
  runnerUp?: BandActivity;
  /** What they are short of. The commonest honest answer to "why". */
  pressures: Pressures;
  /** How the hunting looks, 0..1, over the best ground a party could reach. */
  abundance: number;
  /** Hazard where they stand, 0..1. Over 0.5 nothing else is scored at all. */
  danger: number;
  /** What the ground under them offers a forager, 0..1. */
  forage: number;
  /** How badly wood is wanted beyond the household baseline, 0..1. */
  woodNeed: number;
  /** How hard an unexplained sign is pulling at them — an Omen's own row. */
  drawn: number;
  /** How much a place they hold sacred has come to matter, when they hold one. */
  regard?: number;
  /**
   * How together the band holds, 0..1 — `BandState.cohesion`, read straight
   * off. The card wants it for one sentence, and fission reads the same
   * number, so what the card warns of is exactly what the split will cite.
   */
  cohesion: number;
  /**
   * Consecutive days below the fission line (2026-08-23). Zero for a band
   * holding together; climbing toward FISSION_DAYS (45) for one that is
   * genuinely coming apart. The purest check-tomorrow fact the sim computes,
   * and until now nothing on any card said it.
   */
  frayedDays: number;
}

export class BandSystem implements SimSystem {
  private readonly seed: number;
  private readonly habitat: HabitatSampler;
  private readonly wildlife: WildlifeReader;
  private readonly events: EventBus;
  private readonly land: LandWriter;
  private readonly hazards: Hazards;
  private readonly anchors: AnchorReader;
  private readonly cultures: CultureSystem;
  /** Days each pair of bands has spent in one another's country. */
  private readonly contacts = new Map<string, number>();
  private readonly list: Band[] = [];
  private readonly dirty = new Set<string>();
  private readonly construction: ConstructionSystem;
  private readonly pathSys: PathSystem;
  private readonly pressure = new MortalPressure();
  private nextId = 0;

  constructor(opts: {
    seed: number;
    habitat: HabitatSampler;
    wildlife: WildlifeReader;
    events: EventBus;
    land: LandWriter;
    hazards: Hazards;
    /** Places gods have bound themselves to. Mortals find these on foot. */
    anchors?: AnchorReader;
    /** Who these peoples are. Bands are born into one and daughters inherit it. */
    cultures?: CultureSystem;
    /**
     * Whether ground can carry a structure — dry land, not a river. Handed
     * through to construction for its far placement stations. Absent (as in
     * a probe), everywhere carries.
     */
    buildable?: (x: number, z: number) => boolean;
  }) {
    this.seed = opts.seed;
    this.habitat = opts.habitat;
    this.wildlife = opts.wildlife;
    this.events = opts.events;
    this.land = opts.land;
    this.hazards = opts.hazards;
    this.anchors = opts.anchors ?? NO_ANCHORS;
    this.cultures = opts.cultures ?? new CultureSystem(opts.seed);
    // Construction stamps the roof shape onto every frame it opens (§50). The
    // culture system is the only thing that knows it and construction has no
    // business holding one, so it is handed the single question it needs.
    this.construction = new ConstructionSystem(
      opts.seed, opts.events,
      (b) => this.cultures.get(b.cultureId)?.practices.building ?? 'round',
      // the worn ways, for wayside shelter plots. Lazy through `this`: the
      // path system is built one line down, and the closure only runs during
      // an advance, long after both exist.
      (x, z, range) => {
        const out: Array<{ x: number; z: number }> = [];
        for (const p of this.pathSys.paths()) {
          for (const pt of p.points) {
            if (Math.hypot(pt.x - x, pt.z - z) <= range) out.push(pt);
          }
        }
        return out;
      },
      opts.buildable,
    );
    this.pathSys = new PathSystem(opts.events);
  }

  /**
   * Found a band. Traits come from a generator seeded by (spawn index, world
   * seed): spawn order is part of world history, so identical histories found
   * identical bands. Starts with three days of food and full waterskins.
   */
  spawn(
    x: number, z: number, population: number, now: number,
    /** The people this band belongs to. A new one is founded if none is given. */
    culture?: CultureState,
  ): BandState {
    const idx = this.nextId++;
    const rng = mulberry32(hash3(idx, 0x6a09, this.seed));
    // A band is born holding whatever its culture believed at the moment it
    // was founded, give or take a community's own character. This is how a
    // people propagates: two bands of the Ashfolk resemble each other because
    // they inherited the same leanings, not because anything copies them
    // afterwards — from here they are free to differ.
    const people = culture ?? this.cultures.found(now);
    const jitter = () => (rng() - 0.5) * 0.24;
    const band: Band = {
      id: `band-${idx}`,
      idHash: hash3(idx, 0xb17d, this.seed),
      population,
      x, z,
      homeX: x, homeZ: z,
      foundedAt: now,
      cultureId: people.id,
      reserves: { food: population * 3, water: population * WATER_CAP_DAYS, wood: 0 },
      traits: {
        curiosity: clamp(people.leanings.curiosity + jitter(), 0.05, 0.95),
        caution: clamp(people.leanings.caution + jitter(), 0.05, 0.95),
        mobility: clamp(people.leanings.mobility + jitter(), 0.05, 0.95),
        tradition: clamp(people.leanings.tradition + jitter(), 0.05, 0.95),
      },
      cohesion: 0.7,
      frayedDays: 0,
      cultureApart: 0,
      uncertainty: 0.6,
      waterlessDays: 0,
      activity: 'camp',
      // one party to begin with: everybody, at home, doing nothing yet. The
      // planner splits them as needs appear.
      parties: [{
        id: `band-${idx}-p0`, activity: 'camp', size: population, x, z,
        walkKey: '', trace: [], movedToday: 0,
        foragedToday: false, gatheredToday: false,
      }],
      nextParty: 1,
      // Founded with an age pyramid, not as a crèche. Everyone used to be born
      // on the founding day, which made the island's median age eight and put
      // nobody over thirty — see `foundingAge`.
      people: Array.from({ length: Math.round(population) }, (_, i) =>
        makePerson(`band-${idx}`, hash3(idx, PERSON_SALT, this.seed), i,
                   foundingBornAt(hash3(idx, PERSON_SALT, this.seed), i, now))),
      nextPerson: Math.round(population),
      knownPlaces: [],
      memories: [],
      anomalies: [],
      sacred: [],
      // signs start on credit, not on proof: a band that has never been
      // shown anything is willing to go and look once
      omenTrust: 0.6,
      migrating: false,
      shortageOpen: false,
    };
    this.rememberPlace(band, now);
    this.list.push(band);
    return band;
  }

  /**
   * Walk every absolute hour boundary in (from, to]. Hours divide both the
   * decision grid and the daily grid, so all three cadences fire in one
   * fixed order per boundary — split the span anywhere and nothing changes.
   */
  advance(from: number, to: number): void {
    if (!(to > from)) return;
    let k = Math.floor(from / HOUR) + 1;
    for (let t = k * HOUR; t <= to; t = ++k * HOUR) {
      this.hourly(t);
      if ((k * HOUR) % DECISION_STEP === 0) this.sixHourly(t, (k * HOUR) / DECISION_STEP);
      if ((k * HOUR) % DAY === 0) this.daily(t);
    }
  }

  bands(): readonly BandState[] {
    return this.list;
  }

  /** Everything standing, ruins included — structures are never deleted. */
  structures(): readonly StructureState[] {
    return this.construction.structures();
  }

  /** Structures under construction: materials in, labor in, roof not yet. */
  workSites(): readonly WorkSite[] {
    return this.construction.workSites();
  }

  /** The paths repeated travel has worn into the land. */
  paths(): readonly PathRecord[] {
    return this.pathSys.paths();
  }

  /**
   * The band's own functional fires within reach of home, nearest home first
   * (ties by id). The residual camp groups muster one per fire, in this
   * order — so the primary fire fills first and the outer fires are lived at
   * rather than lit and left.
   */
  private ownFires(b: BandState): Array<{ x: number; z: number }> {
    const out: Array<{ x: number; z: number; d: number; id: string }> = [];
    for (const s of this.construction.structures()) {
      if (s.type !== 'hearth' || s.condition < FUNCTIONAL || s.ownerBandId !== b.id) continue;
      const d = Math.hypot(s.x - b.homeX, s.z - b.homeZ);
      if (d <= HEARTH_RANGE) out.push({ x: s.x, z: s.z, d, id: s.id });
    }
    return out
      .sort((a, c) => (a.d - c.d) || (a.id < c.id ? -1 : 1))
      .map((f) => ({ x: f.x, z: f.z }));
  }

  /** Bumps whenever the path set changes — renderers rebuild geometry on change. */
  pathsVersion(): number {
    return this.pathSys.version;
  }

  /** Mortal use of the ground under a point, decayed to now. Null where negligible. */
  pressureAt(wx: number, wz: number, now: number): MortalPressureCell | null {
    return this.pressure.at(wx, wz, now);
  }

  /** Cell count of the pressure ledger — the renderers' skip-it-all hint. */
  pressureSize(): number {
    return this.pressure.size;
  }

  /** Every chunk mortal wear tints. Adopting a world is the only caller that
   *  needs it — see `MortalPressure.chunks`. */
  pressureChunks(): string[] {
    return this.pressure.chunks();
  }

  /**
   * Hearths that are still someone's — "the camp is lived in", for renderers.
   *
   * Keyed on the owning band's HOME, not on where its feet happen to be:
   * foragers and hunters are out most of the day, and a fire that only smoked
   * while everybody stood around it meant the one signal that says *people
   * live here* was almost never lit. A hearth smokes while the band that
   * built it still calls this place home, and goes cold when they leave.
   */
  activeHearths(): string[] {
    const out: string[] = [];
    for (const s of this.construction.structures()) {
      if (s.type !== 'hearth' || s.condition < 0.2) continue;
      if (this.list.some((b) =>
        b.population > 0 && Math.hypot(b.homeX - s.x, b.homeZ - s.z) <= HEARTH_RANGE)) {
        out.push(s.id);
      }
    }
    return out;
  }

  /** Chunk keys ("cx,cz") whose land the bands changed since last drained. */
  drainDirtyChunks(): string[] {
    const out = [...this.dirty].sort();
    this.dirty.clear();
    return out;
  }

  /**
   * The whole mortal layer, copied out (spec §49): bands with their walk and
   * day bookkeeping, structures and work sites with the counters that open
   * them, paths with the traversal counts still earning one, and the
   * pressure ledger. Id cursors ride along — site placement hashes nextId,
   * so a save that lost it would raise tomorrow's hearth somewhere else.
   */
  toSnapshot(): BandSystemSnapshot {
    const con = this.construction as unknown as ConstructionInternals;
    const ps = this.pathSys as unknown as PathInternals;
    const pr = this.pressure as unknown as PressureInternals;
    return {
      nextId: this.nextId,
      bands: this.list.map(cloneBand),
      construction: {
        nextId: con.nextId,
        structures: con.structureList.map((s) => ({ ...s })),
        sites: con.siteList.map((s) => ({
          ...s, required: { ...s.required }, delivered: { ...s.delivered },
        })),
        counters: [...con.counters].map(([bandId, c]) => ({ bandId, ...c })),
      },
      paths: {
        nextId: ps.nextId,
        version: ps.versionCounter,
        paths: ps.list.map((p) => ({ ...p, points: p.points.map((q) => ({ ...q })) })),
        pairs: [...ps.pairs].map(([key, e]) => e.pathId !== undefined
          ? { key, count: e.count, pathId: e.pathId }
          : { key, count: e.count }),
      },
      pressure: [...pr.cells.values()].map((c) => ({ ...c })),
      // who has been living next to whom: a save that lost this would resume
      // with every band a stranger to every other, and two communities on the
      // brink of becoming one would start their acquaintance again
      contacts: [...this.contacts].map(([key, days]) => ({ key, days })),
    };
  }

  /** Replace the whole mortal layer in place. Shares nothing with the source. */
  restore(snap: BandSystemSnapshot): void {
    const con = this.construction as unknown as ConstructionInternals;
    const ps = this.pathSys as unknown as PathInternals;
    const pr = this.pressure as unknown as PressureInternals;

    this.list.length = 0;
    for (const b of snap.bands) this.list.push(cloneBand(b));
    this.nextId = snap.nextId;
    this.dirty.clear();

    con.structureList.length = 0;
    for (const s of snap.construction.structures) con.structureList.push({ ...s });
    con.siteList.length = 0;
    for (const s of snap.construction.sites) {
      con.siteList.push({ ...s, required: { ...s.required }, delivered: { ...s.delivered } });
    }
    con.counters.clear();
    for (const c of snap.construction.counters) {
      con.counters.set(c.bandId, { settledDays: c.settledDays, fullDays: c.fullDays });
    }
    con.nextId = snap.construction.nextId;

    ps.list.length = 0;
    for (const p of snap.paths.paths) {
      ps.list.push({ ...p, points: p.points.map((q) => ({ ...q })) });
    }
    ps.pairs.clear();
    for (const e of snap.paths.pairs) {
      ps.pairs.set(e.key, e.pathId !== undefined
        ? { count: e.count, pathId: e.pathId }
        : { count: e.count });
    }
    ps.versionCounter = snap.paths.version;
    ps.nextId = snap.paths.nextId;

    pr.cells.clear();
    for (const c of snap.pressure) pr.cells.set(`${c.gx},${c.gz}`, { ...c });

    this.contacts.clear();
    for (const c of snap.contacts ?? []) this.contacts.set(c.key, c.days);
  }

  private habAt(x: number, z: number, t: number): SocialHabitat {
    const { sx, sz } = toSocial(x, z);
    return this.habitat.at(sx, sz, t);
  }

  /** Movement and continuous activity effects, at the top of every hour. */
  private hourly(t: number): void {
    for (const b of this.list) {
      if (b.population <= 0) continue;
      const pop = Math.max(1, b.population);
      // every party walks its own hour and works where that hour leaves it
      for (const p of b.parties) {
        this.walkParty(b, p, t);
        this.partyWork(b, p, t, p.size / pop);
      }
      // and the band stands where its people are, weighted by how many. This
      // is what construction reads to ask whether the community is home, and
      // what witnessing reads to ask whether it could have seen something.
      let wx = 0, wz = 0, tot = 0;
      for (const p of b.parties) { wx += p.x * p.size; wz += p.z * p.size; tot += p.size; }
      if (tot > 0) { b.x = wx / tot; b.z = wz / tot; }
      // anyone home with news the hearth has not heard tells it now
      this.deliverNews(b, t);
    }
  }

  /**
   * Bring the roster level with the number (v0.3.2 §39). `population` is the
   * economy's variable and moves in fractions; people are whole. So when the
   * number rises past the roster somebody is born, and when it falls below,
   * somebody in particular dies — the frailest, which is what hardship does.
   * A death is a real loss of a named life, not a decrement.
   */
  /**
   * Ordinary mortality, at the day boundary (Design III §139).
   *
   * Age decides, `sim/mortals/mortality.ts` says how, and the draw is a hash
   * of who the person is and which absolute day it is — so a split advance
   * buries exactly the same people as a whole one, which
   * `test/simulation-catchup.ts` requires.
   *
   * A death is `population - 1` *and* the person off the roster, together:
   * the roster is kept equal to `round(population)`, so removing one without
   * the other means the next reconcile silently undoes it.
   *
   * The last member of a band is not exempt. A band that dies out leaves what
   * it built standing and unowned — §30 already handles that, and a lineage
   * ending is exactly the history this world could not previously record.
   */
  private reap(b: Band, t: number): void {
    const day = Math.floor(t / DAY_SECONDS);
    for (let i = b.people.length - 1; i >= 0; i--) {
      const p = b.people[i];
      const age = (t - p.bornAt) / (365 * DAY_SECONDS);
      if (!died(idSeed(p.id), day, age, p.vitality)) continue;
      b.people.splice(i, 1);
      b.population = Math.max(0, b.population - 1);
      // An old death carried `resource: 'life'` on a `resource_shortage`, on
      // the reasoning that starvation already emitted that shape and one
      // reader could serve both. It could not: the Chronicle rendered every
      // death on this island as "hunger took hold", and ordinary mortality is
      // not hunger — the frailest dying of age in a band with eight days of
      // food in the larder is the opposite of a famine. `resource_shortage`
      // still means what it says; a death is its own event now.
      const rec = this.events.emit({
        type: 'person_died', time: t, x: b.x, z: b.z,
        radius: 260, magnitude: 1,
        // an elder's death is a thing a community marks; an infant's, in a
        // world where 40% do not reach fifteen, is grief and not news
        salience: age >= 45 ? 0.62 : age >= 15 ? 0.5 : 0.38,
        cause: { kind: 'mortal', bandId: b.id },
        data: { who: p.name, id: p.id, age: Math.round(age), of: 'age' },
      });
      this.mourn(b, p, rec, t);
    }
  }

  /**
   * A PERSON STOPPED BEING WHO THEY WERE — `person_changed`, the world's own
   * word for an axis crossing a card line (`STANDS_OUT` / `STANDS_APART`).
   *
   * Emitted for any cause. A life's work crosses these lines far more often
   * than a god does, and law 4 wants promotion reachable by natural history;
   * so the daily pass emits it with no parent, and `press` hands its
   * crossings back to `Simulation.act`, which emits the same event with the
   * cast as parent — the one road a pressed life has to the Chronicle.
   * Salience is under `SALIENT` on purpose: somebody growing bold is not a
   * thing the hearth remembers as an event, and the trace is on the person.
   */
  became(bandId: string, c: Crossing, t: number, parentEventIds?: string[]): void {
    const b = this.list.find((x) => x.id === bandId);
    if (!b) return;
    const party = b.parties.find((q) => q.id === c.person.partyId);
    this.events.emit({
      type: 'person_changed', time: t,
      x: party?.x ?? b.x, z: party?.z ?? b.z,
      radius: 40, magnitude: 1, salience: 0.3,
      cause: { kind: 'mortal', bandId: b.id },
      data: { who: c.person.name, id: c.person.id, axis: c.axis, up: c.up, targetId: c.person.id },
      ...(parentEventIds ? { parentEventIds } : {}),
    });
  }

  /**
   * A DEATH IS FELT BY THE PEOPLE IT WAS TIED TO (`PLAYABLE.md` P2).
   *
   * Until now a death touched the survivors only by absence: `parentId`
   * stopped resolving, a bond went on fading toward somebody who was not
   * there, and the card could say nothing truer than *not here now* — the
   * same words it uses when half a band walks away in a split. The world
   * knew the difference and kept it from everybody.
   *
   * Two things happen here, and both are the tied person's own:
   *
   *  · **They know who they lost.** `mourn` writes the dead person's name and
   *    what they were — parent, child, companion — onto each of them, so the
   *    card can say *child of Tolun, who died* rather than guess.
   *  · **They hold it harder.** Everyone at the hearth remembers a death at
   *    the GRIM weighting; the bereaved hold it at `GRIEF` times that, which
   *    puts it at the top of what THEY carry and leaves a neighbour's copy
   *    where it was. A party member out of earshot is given the memory
   *    unsaid, to carry home: a death in the family reaches you.
   *
   * Nothing here reads another band, another person's traits, or the clock
   * beyond `t`, and the bereaved are found from ids alone — so the order
   * nothing is processed in can change nothing (law 7). No god is anywhere
   * near it; a death is the world's own.
   */
  private mourn(b: Band, gone: PersonState, death: EventRecord, t: number): void {
    for (const { person, tie } of bereavedBy(gone, b.people)) {
      mourn(person, gone, tie, t);
      const held = person.memories.find((m) => m.eventId === death.id);
      if (held) {
        held.importance = clamp(held.importance * GRIEF, 0, 1);
      } else {
        this.remember(person, {
          eventId: death.id, time: t, kind: 'told',
          importance: clamp(death.salience * memorability(death) * GRIEF, 0, 1),
          x: death.x, z: death.z, told: false,
        });
      }
    }
  }

  private reconcilePeople(b: Band, t: number): void {
    const want = Math.round(b.population);
    while (b.people.length < want) {
      // THE BAND'S OWN identity, not `this.nextId`.
      //
      // Until now a birth seeded from the BandSystem's global count of bands
      // ever spawned, so who was born next in every band on the island changed
      // whenever any band was founded anywhere — and because `split` hands a
      // daughter its parent's `nextPerson`, a parent and its daughter minted
      // the next child from an identical (seed, idx) pair: hash twins, same
      // name, same temperament, same vitality, differing only in the id
      // prefix. `b.idHash` is what `planParties` already seeds people with.
      const seed = hash3(b.idHash, PERSON_SALT, this.seed);
      // and a child takes after somebody, which is the whole of §4.1's
      // inheritance: whoever of an age the day chose, pulled a quarter of the
      // way toward what is ordinary in this camp, plus variation.
      const parent = chooseParent(b.people, seed, b.nextPerson, t);
      const born = parent
        ? makeChild(b.id, seed, b.nextPerson++, t, parent, rosterNorm(b.people))
        : makePerson(b.id, seed, b.nextPerson++, t);
      b.people.push(born);
      // A birth emitted nothing whatever until now, which made the single most
      // common thing that happens to this island's people invisible to the
      // Chronicle, to witnessing, and to every attachment mechanic built on
      // either. It is quiet — a birth is news in one camp and nowhere else —
      // but quiet is not the same as silent.
      this.events.emit({
        type: 'person_born', time: t, x: b.x, z: b.z,
        radius: 200, magnitude: 1, salience: 0.34,
        cause: { kind: 'mortal', bandId: b.id },
        data: { who: born.name },
      });
    }
    while (b.people.length > want && b.people.length > 0) {
      // the frailest goes; ties break on roster order, which is founding order
      let weakest = 0;
      for (let i = 1; i < b.people.length; i++) {
        if (b.people[i].vitality < b.people[weakest].vitality) weakest = i;
      }
      const [gone] = b.people.splice(weakest, 1);
      // this path IS the hungry one — `reconcilePeople` only takes a life when
      // the economy has driven `population` below the roster, and starvation
      // and thirst are the only two things that do that. So it says so, and
      // `reap` above (ordinary age) no longer borrows the same words.
      const rec = this.events.emit({
        type: 'person_died', time: t, x: b.x, z: b.z,
        radius: 260, magnitude: 1, salience: 0.68,
        cause: { kind: 'mortal', bandId: b.id },
        data: { who: gone.name, id: gone.id, of: 'want' },
      });
      this.mourn(b, gone, rec, t);
    }
  }

  /** One party's hour of walking — and the trail it wears by doing it. */
  private walkParty(b: Band, p: Party, t: number): void {
    if (!p.target) return;
    // a fresh destination starts a fresh walk — its trace is the path-to-be
    const tk = `${p.target.x},${p.target.z}`;
    if (p.walkKey !== tk) {
      p.walkKey = tk;
      p.trace = [{ x: p.x, z: p.z }];
    }
    // only a party carrying the whole community is the community moving
    const whole = p.size >= Math.round(b.population);
    const mode: MoveMode = p.activity === 'flee' ? 'flee'
                         : (b.migrating && whole) ? 'migrate' : 'normal';
    // a worn way that serves the trip is USED: the walker follows its
    // polyline between the join and the leave point, and the speed bonus
    // belongs to feet actually on it. Fleeing runs straight — nobody keeps
    // to the road with a fire behind them. Recomputed each hour from
    // (position, target): stateless, saved nowhere, identical after restore.
    const route = mode === 'flee'
      ? null
      : this.pathSys.waypointsFor({ x: p.x, z: p.z }, p.target);
    const legs = route
      ? [...route.points, { x: p.target.x, z: p.target.z }]
      : [{ x: p.target.x, z: p.target.z }];
    const mul = route?.onPath ? PATH_SPEED : 1;
    const r = stepAlong(p, legs, mode, HOUR, mul);
    p.movedToday += r.moved;
    const last = p.trace[p.trace.length - 1];
    if (r.moved > 0 &&
        (r.arrived || Math.hypot(p.x - last.x, p.z - last.z) >= PATH_SAMPLE)) {
      p.trace.push({ x: p.x, z: p.z });
    }
    // whatever they walked past, they walked past — a god's place is found
    // by feet and nothing else
    if (r.moved > 0) this.noticeAnchors(b, p, t);
    if (!r.arrived) return;

    // a finished walk is a traversal: the third one wears a path
    for (const k of this.pathSys.recordTravel(p.trace, t, this.land)) this.dirty.add(k);
    p.walkKey = '';
    p.trace = [];
    delete p.target;
    if (b.migrating && whole) {
      b.migrating = false;
      delete b.target;
      this.events.emit({
        type: 'band_arrived', time: t, x: p.x, z: p.z,
        radius: 300, magnitude: b.population, salience: 0.35,
        cause: { kind: 'mortal', bandId: b.id },
      });
      p.activity = 'camp';
    } else if (p.activity === 'travel' || p.activity === 'flee') {
      p.activity = 'camp';
    } else if (p.activity === 'venerate') {
      const s = b.sacred.find((q) => Math.hypot(p.x - q.x, p.z - q.z) <= 90);
      if (s) this.venerate(b, s, t);
      // and then they go home. A visit is a *trip*: measured without this,
      // the party simply stayed at the stone and the band spent 95% of its
      // hours standing there — one arrival, no second visit ever, the ladder
      // frozen and the foraging abandoned. Walking back is also what wears
      // the first rung into the ground: the footpath is a real path.
      p.activity = 'camp';
      p.target = { x: b.homeX, z: b.homeZ };
    } else if (p.activity === 'investigate') {
      // they walked to the place the sign seemed to mean, and it is whatever
      // it is. This is the moment an Omen acquires a meaning, or loses one.
      const sign = this.bestAnomaly(b);
      if (sign && Math.hypot(p.x - sign.x, p.z - sign.z)
                  <= sign.uncertaintyRadius + SEARCHED) {
        this.answerAnomaly(b, sign, p, p.x, p.z, t);
      }
    }
  }

  /**
   * What a party's hour does to the band's stores — and it does it from the
   * ground the party is actually standing on, which is the whole point of
   * parties existing. A forage party out on good grass feeds the band better
   * than the same people would have sitting in camp, and the wood comes from
   * the stand they walked to.
   */
  private partyWork(b: Band, p: Party, t: number, share: number): void {
    const hab = this.habAt(p.x, p.z, t);
    // by water they drink, and fill the skins they carry home
    if (hab.waterAccess > NEAR_WATER) {
      b.reserves.water = Math.min(
        b.population * WATER_CAP_DAYS, b.reserves.water + p.size * 0.4);
    }

    switch (p.activity) {
      case 'forage':
        // spec §10: 0.9 person-days per person per FULL DAY at forage 1.0,
        // times whatever the ground under their hands remembers (see bounty).
        // The divisor is the WORKING day (12 h since nightfall folds the
        // field home), so the spec's daily number survives to the digit.
        b.reserves.food = Math.min(
          b.population * this.construction.foodCapDays(b),
          b.reserves.food +
            (p.size * 0.9 * hab.foragePotential * this.bounty(p.x, p.z, t)) / WORK_HOURS);
        p.foragedToday = true;
        break;
      case 'gather_wood':
        b.reserves.wood = Math.min(
          b.population * 3,
          b.reserves.wood + (p.size * 1.2 * hab.woodyBiomass) / WORK_HOURS);
        p.gatheredToday = true;
        break;
      case 'explore':
        // a scouting party's own ground becomes what the band knows.
        // 0.008 = the old 0.004 x (24 / WORK_HOURS): scouting keeps daylight
        // hours now, and a day of it teaches what a day of it always taught.
        this.recordPlace(b, p.x, p.z, t);
        b.uncertainty = clamp(b.uncertainty - 0.008 * share, 0, 1);
        break;
      case 'rest':
        // Resting mends a band by keeping its people at home, which is what
        // `apart` in settleCohesion already reads. It used to add to cohesion
        // directly as well, and that hourly write beat the daily tracker
        // outright — a band of ninety-three sat at 0.755 while its conditions
        // deserved 0.30, because 0.05 a day of resting outran a 7% pull
        // toward the truth. One mechanism, not two.
        break;
      case 'build': {
        // hands on the frame if close enough; otherwise walk to it. Those
        // hands gather nothing meanwhile — that cost is the whole economy of
        // architecture, and now it costs a party rather than the band.
        const site = this.construction.siteFor(b.id);
        if (!site) {
          p.activity = 'camp';
        } else if (Math.hypot(site.x - p.x, site.z - p.z) <= SITE_RANGE) {
          // 24/WORK_HOURS: frames rise in daylight now, at a day's pace
          this.construction.work(b, site, 24 / WORK_HOURS, t, p.size);
        } else if (!p.target) {
          p.target = { x: site.x, z: site.z };
        }
        break;
      }
      default:
        break;
    }
  }

  /**
   * What the ground under a forager's hands is worth, as a multiplier on
   * the day's gathering (v0.4 — "the land remembers" reaching the larder).
   *
   * Two measurements made this necessary. `tools/strengthen.ts`: a year of hard
   * blessing moved the biomass deviation from −0.44 to +0.84 and the band's
   * stores not at all, so a god's main kindness reached the land and stopped
   * there. And the arithmetic underneath: at forage 1.0 a forager gathers
   * 0.9 person-days and eats 1.0, so foraging is never self-sustaining at
   * *any* richness — and `foragePotential` is a saturating 0..1 average that
   * already sits at 0.887 on ordinary ground, leaving one eighth of a
   * headroom for every blessing a god will ever cast.
   *
   * So the gift is applied here instead: not to the judgement of a place,
   * which stays exactly the 0..1 quantity every other reader expects, but to
   * the yield of the actual soil somebody has their hands in. A blessed
   * field feeds people more than an unblessed field of the same kind.
   *
   * Note what this is NOT: a bonus for having a god. Nothing in the world
   * writes a positive biomass deviation except Strengthen — wear, trampling and
   * fire all write negative — so in a world with no god the multiplier is
   * exactly 1 and the whole economy is bit-for-bit what it was. The god has
   * to have actually done something, on that ground, and recently enough
   * that the land still remembers it.
   */
  private bounty(x: number, z: number, t: number): number {
    const e = this.land.at(x, z, t);
    if (!e) return 1;
    // a withered field is already punished through foragePotential; do not
    // charge twice for it. This term only ever carries a blessing.
    return 1 + Math.max(0, e.biomass) * BOUNTY_GAIN;
  }

  /** Hunt yields land, then minds change — every six hours, absolute grid. */
  private sixHourly(t: number, decisionIndex: number): void {
    // stories cross wherever two bands' people stood this hour, before
    // anybody replans and walks away from the meeting
    this.meetStrangers(t);
    for (const b of this.list) {
      if (b.population <= 0) continue;
      // every hunting party takes from the herd it actually walked to, with
      // the effort of the hands it actually has. This is the difference
      // between a band abstractly "hunting" and four people at a herd.
      for (const p of b.parties) {
        if (p.activity !== 'hunt') continue;
        const { sx, sz } = toSocial(p.x, p.z);
        const grazer = this.wildlife.populationAt(sx, sz, 'grazer');
        const small = this.wildlife.populationAt(sx, sz, 'small_game');
        // hunt whatever the land carries more of
        const species = grazer >= small ? 'grazer' : 'small_game';
        const effort = clamp(p.size / 40, 0.05, 1);
        const gained = this.wildlife.harvest(sx, sz, species, effort, t);
        b.reserves.food = Math.min(
          b.population * this.construction.foodCapDays(b),
          b.reserves.food + Math.max(0, gained));
      }
      // …and at DUSK, while the parties still hold the day's shape, the day
      // makes its people — before planParties folds the field home and the
      // evidence of who worked beside whom at what is gone until morning
      if (decisionIndex % 4 === 3) this.duskLife(b, t);
      // …and what else is out on that ground, which is not always prey
      this.predatorsMeet(b, t, decisionIndex);
      this.decideFor(b, t, decisionIndex);
      this.planParties(b, decisionIndex, t);
    }
  }

  /**
   * The working day, read into its people at the 18:00 boundary
   * (2026-08-23). This block lived at the DAILY boundary and read each
   * person's current party — which was the day's work while parties
   * persisted overnight, and became "camp" for everyone the moment
   * nightfall folded the field home at dusk: a world of hunters whose
   * lives recorded nothing but evenings. Natural crossings measured on a
   * god-free year fell from six to ZERO, and the companion bond never
   * formed, before this moved.
   *
   * Dusk is the honest boundary anyway: the day shapes you as it ends, a
   * newborn (born at midnight) is first shaped by the first full day it
   * lived, and the dead were reaped before ever being asked. Same absolute
   * grid, once per day, split-vs-whole exact.
   */
  private duskLife(b: BandState, t: number): void {
    const activity = new Map<string, BandActivity>();
    for (const p of b.parties) activity.set(p.id, p.activity);
    const axesBefore = axesOf(b.people);
    liveADay(b.people, (p) => (p.partyId ? activity.get(p.partyId) : undefined), t);
    // …and anyone a day's living carried over a line the card speaks at
    // has become something: the world says so, in the same event a god's
    // press would have used, with no parent because nobody pressed
    for (const c of crossingsSince(b.people, axesBefore, t)) this.became(b.id, c, t);

    // …and a day of work is also a day of somebody's company.
    //
    // `partyId` is deleted and reassigned from scratch every planning cycle
    // (`assignPeople`), so co-membership accumulated NOTHING before this: two
    // people could spend a decade on the same errands and the world held no
    // trace of it. This is the trace, and it is the first relationship in the
    // build that is not lineage.
    //
    // Rosters are gathered in the band's own people order rather than the
    // party's, so the pair loop is over a fixed order and cannot depend on
    // how `assignPeople` happened to fill the list.
    const crew = new Map<string, PersonState[]>();
    for (const p of b.people) {
      if (!p.partyId) continue;
      const c = crew.get(p.partyId);
      if (c) c.push(p); else crew.set(p.partyId, [p]);
    }
    for (const party of b.parties) {
      const c = crew.get(party.id);
      if (!c) continue;
      shareADay(c, party.activity);
      // …and of somewhere: the ground the errand worked, which is what
      // the card means by *where do you go repeatedly*
      const { sx, sz } = toSocial(party.x, party.z);
      workADay(c, party.activity, `${sx},${sz}`);
    }
  }

  /**
   * WHEN TWO BANDS' PEOPLE STAND ON THE SAME GROUND, A STORY CROSSES.
   *
   * See `MEET_RANGE` for the measurement and the bounds. The shape here is
   * the one every other exchange in this file has: read everything first,
   * write afterwards, so the order the pairs are walked in cannot change what
   * anybody hears (law 7). Two bands' parties may meet several times in one
   * hour; one meeting per pair counts — the nearest pair of parties — and one
   * story goes each way.
   *
   * What goes is a PERSON's memory, not the band's ledger: knowledge moves
   * only inside heads, never across tiles, which is the whole mechanism
   * religion will later spread by (`deliverNews`). The listener carries it
   * home unsaid (`told: false`) and the hearth hears it the hour they are
   * back, by the same road any party's news takes — so a sign heard of from
   * strangers becomes the hearing band's own anomaly to explain, measured
   * from THEIR camp, and the decision table can send them to look at a place
   * their own people never saw. That is the far end of `PLAYABLE.md`'s chain:
   * something happened that the god did not command, in a community the god
   * never touched.
   */
  private meetStrangers(t: number): void {
    const live = this.list.filter((b) => b.population > 0.5);
    type Telling = { to: PersonState[]; from: Band; m: PersonMemory };
    const tellings: Telling[] = [];
    for (let i = 0; i < live.length; i++) {
      for (let j = i + 1; j < live.length; j++) {
        const A = live[i], B = live[j];
        // the nearest pair of parties decides whether they met this hour
        let pa: Party | undefined, pb: Party | undefined, best = Infinity;
        for (const x of A.parties) for (const y of B.parties) {
          const d = Math.hypot(x.x - y.x, x.z - y.z);
          if (d < best) { best = d; pa = x; pb = y; }
        }
        if (!pa || !pb || best > MEET_RANGE) continue;
        const ofA = A.people.filter((p) => p.partyId === pa!.id);
        const ofB = B.people.filter((p) => p.partyId === pb!.id);
        if (ofA.length === 0 || ofB.length === 0) continue;
        const a2b = this.storyFor(ofA, ofB, B, t);
        const b2a = this.storyFor(ofB, ofA, A, t);
        if (a2b) tellings.push({ to: ofB, from: A, m: a2b });
        if (b2a) tellings.push({ to: ofA, from: B, m: b2a });
      }
    }
    for (const { to, from, m } of tellings) {
      const importance = clamp(m.importance * SECOND_HAND, 0, 1);
      for (const listener of to) {
        const held = listener.memories.find((h) => h.eventId === m.eventId);
        if (held) {
          // the camp that heard distant thunder learns what it was when
          // somebody who stood under it walks by — a fuller account deepens
          // a fainter one, exactly as `bandRemember` lets a telling deepen
          // the hearth's record; and it is news again, to be carried home
          if (held.importance >= importance) continue;
          held.importance = importance;
          held.told = false;
          held.from = from.id;
          if (m.radius !== undefined) held.radius = m.radius;
          continue;
        }
        this.remember(listener, {
          // dated to the hearing, as the hearth's own retellings are; the
          // event's own moment is in its id
          eventId: m.eventId, time: t, kind: 'told', importance,
          x: m.x, z: m.z,
          // unsaid: it walks home in this head and the hearth hears it then
          told: false,
          ...(m.radius !== undefined ? { radius: m.radius } : {}),
          from: from.id,
        });
      }
    }
  }

  /**
   * The one story a party would tell strangers: the memory among its people
   * that is held hardest now and spreads most readily, and that the listeners
   * — and their whole community — do not already hold as well as the telling
   * would leave them. Nothing, most hours: neighbours on shared ground have
   * long since heard each other out.
   */
  private storyFor(
    tellers: readonly PersonState[], listeners: readonly PersonState[],
    listening: Band, now: number,
  ): PersonMemory | null {
    let best: PersonMemory | null = null, top = 0;
    for (const p of tellers) {
      // `0.5 + sociability` so the least forthcoming still tell (0.65×) and
      // the most forthcoming are heard a third again as strongly (1.35×) — a
      // tilt on WHICH story crosses, never a gate on whether one does
      const spread = 0.5 + p.traits.sociability;
      for (const m of p.memories) {
        const score = hold(m.importance, m.time, now) * spread;
        if (score <= top) continue;
        const carried = m.importance * SECOND_HAND;
        const knows = (h: { eventId: string; importance: number }) =>
          h.eventId === m.eventId && h.importance >= carried;
        if (listening.memories.some(knows)) continue;
        if (listeners.some((l) => l.memories.some(knows))) continue;
        top = score; best = m;
      }
    }
    return best;
  }

  /**
   * WHEN THE PEOPLE OUT ON THE GROUND MEET SOMETHING THAT HUNTS.
   *
   * `predator_incident` has been declared in `sim/events.ts`, carried in this
   * file's own GRIM set, and given both a Chronicle phrase and a faded phrase
   * — and **nothing has ever emitted it**. `tools/tempo.ts` says so out loud:
   * *declared in events.ts and emitted 0 times in this run*. The whole
   * consequence chain existed except its cause, which is the exact shape
   * `WIRING.md` was written to catch.
   *
   * ## The smallest honest condition
   *
   * Deliberately small. This is not a raiding system and it takes nobody: the
   * consequence is **fear and memory**, which is precisely what the machinery
   * downstream already consumes. Every term is state this loop already has in
   * hand, so nothing new is simulated to make it possible:
   *
   *  · **Out on the ground, not at the hearth.** A party keeping the camp is
   *    standing at a fire, and the one thing every predator on earth agrees
   *    about is fire. `AT_CAMP` is the same set `people.ts` calls unasked work.
   *  · **Predators actually standing there**, read from the party's own social
   *    cell — `populationAt` already answers for `'predator'` today, because
   *    `WildlifeReader` types the species as a string. No new interface.
   *  · **Numbers matter, both ways.** Risk rises with how many are hunting
   *    that ground and falls with how many people are together, because a
   *    party of twelve is not approached and two people gathering wood are.
   *
   * The roll is `hash01(band, party, absolute decision index, seed)` — the
   * same construction every other decision here uses — so it is replayable,
   * order-independent, and identical however the span was sliced (law 7).
   *
   * ## What it deliberately does NOT do
   *
   * It does not touch `Hazards.dangerAt`, which is wired to fire alone. That
   * would be the obvious next move and it is the wrong one to make without
   * measuring first: `decide()` short-circuits straight to `flee` above 0.5,
   * and the flee logic is fire-shaped. A band that bolts every time a wolf is
   * in the next cell is worse than a band that does not know. The coupling is
   * a second step with a probe in front of it.
   */
  private predatorsMeet(b: Band, t: number, decisionIndex: number): void {
    for (let i = 0; i < b.parties.length; i++) {
      const p = b.parties[i];
      if (AT_CAMP.has(p.activity)) continue;
      const { sx, sz } = toSocial(p.x, p.z);
      const hunters = this.wildlife.populationAt(sx, sz, 'predator');
      if (hunters <= 0) continue;
      // How exposed this party is: more of them out there, fewer of us.
      // `PARTY_SAFETY` is what one person is worth against one predator, and
      // the shape is a saturation rather than a ratio so that a huge pack does
      // not become a certainty — a party can be unlucky, never doomed.
      const exposure = hunters / (hunters + Math.max(1, p.size) * PARTY_SAFETY);
      const roll = hash01(b.idHash + i * 977, decisionIndex, this.seed + 0x9e37);
      if (roll >= exposure * PREDATOR_RATE) continue;
      /**
       * Salience has to clear `SALIENT` (0.35) or `worthRemembering` drops it
       * and the whole point is lost — the event exists to be *carried*. It
       * rises with exposure so that a close call in thin country is a smaller
       * thing than a pack standing over a wood-gathering pair.
       *
       * Law 10 as always: `cause` is natural, because it IS natural. Nothing
       * about a god is recorded here, and there is nothing to record.
       */
      this.events.emit({
        type: 'predator_incident', time: t, x: p.x, z: p.z,
        radius: PREDATOR_REACH,
        magnitude: hunters,
        salience: clamp(0.45 + exposure * 0.4, 0, 1),
        cause: { kind: 'natural' },
      });
    }
  }

  /**
   * Split the band into parties (v0.3.2 §5). The band has already chosen what
   * it most needs — that scoring is tuned and stays — and this decides how many
   * people that takes and who else goes where at the same time. A community of
   * twenty-five was never one creature doing one thing; foragers can be out on
   * the grass while two people work a shelter frame and somebody fetches water.
   *
   * Existing parties are reused when their activity survives the replan, so a
   * walk already under way keeps its trace and its trail. Sizes always sum to
   * the population, and every number here comes from band state alone — no
   * habitat reads, no dice — so the split is a pure function of the band.
   */
  private planParties(b: Band, decisionIndex: number, t: number): void {
    const pop = Math.max(1, Math.round(b.population));
    const foodDays = b.reserves.food / pop;
    const waterDays = b.reserves.water / pop;
    // NIGHTFALL: at the dark boundaries the field comes home. No re-muster,
    // no disbanding — each field party turns for the fire and walks (the
    // arrival flow folds it into camp), hunts and marches keep their own
    // hours, and the hungry and the parched work the dark (see WORK_HOURS).
    if (darkBoundary(decisionIndex) &&
        b.activity !== 'travel' && b.activity !== 'flee' &&
        foodDays >= 1 && waterDays >= 0.8) {
      for (const p of b.parties) {
        if (!FOLDS_AT_DUSK.has(p.activity)) continue;
        p.activity = 'travel';
        p.target = { x: b.homeX, z: b.homeZ };
      }
      return;
    }
    const site = this.construction.siteFor(b.id);
    const woodShort = site
      ? site.required.wood - site.delivered.wood > b.reserves.wood
      : b.reserves.wood < pop;

    // when the whole community moves, it moves together — one party, everyone
    const wholeBand = b.activity === 'travel' || b.activity === 'flee';
    const want = new Map<BandActivity, number>();
    const ask = (a: BandActivity, n: number) =>
      want.set(a, Math.min(pop, (want.get(a) ?? 0) + Math.max(1, n)));

    if (wholeBand) {
      ask(b.activity, pop);
    } else {
      // A hungry band puts nearly everyone on food; a comfortable one can
      // spare hands for wood and frames. This share is load-bearing: at a flat
      // 45% the dominant party gathered for eleven people while all
      // twenty-five ate, and the band starved on ground that used to feed it.
      const urgency = clamp(1 - foodDays / 5, 0, 1);
      const feeding = b.activity === 'forage' || b.activity === 'hunt';
      ask(b.activity, Math.max(2, Math.round(pop * (feeding ? 0.45 + 0.45 * urgency : 0.45))));
      // a hungry band hunts *and* forages — one community, two food strategies
      // at once, which is the whole reason parties exist. Before this, hunting
      // only ever happened when it out-scored foraging outright, so a band on
      // rich forage ground never sent hunters at all and lived on berries.
      if (foodDays < 4 && b.activity !== 'hunt') ask('hunt', Math.round(pop * 0.2));
      if (waterDays < 1.2) ask('seek_water', Math.round(pop * 0.16));
      if (woodShort) ask('gather_wood', Math.round(pop * 0.16));
      if (site && foodDays > 3) ask('build', Math.round(pop * 0.16));
      if (foodDays > 6 && b.traits.curiosity > 0.55) ask('explore', 2);
      // A few go back to the stone, and the rest keep working.
      //
      // Exactly the fault the hunt line above records, in the same place: a
      // band's decision table picks ONE activity, so veneration could only ever
      // happen by out-scoring foraging outright. Measured with
      // `sim.bands.explain` on the shipped island: forage sits at p50 1.272 and
      // hunt at 1.241 while a known sacred place scores about 0.4, so the only
      // world where anyone visits a stone is one where the band is fed well
      // enough for foraging to collapse — and this island's bands sit at ~3.5
      // days of food for ever. `tools/anchor.ts 200` reads the result: 1–2
      // visits and stage `bare` across all nine curiosity × tradition
      // combinations, while the same stone with fortunes pinned at seven days
      // reaches 46 visits and a roof. The ladder was never broken; nobody could
      // afford the first rung.
      //
      // So it stops competing with eating. Two or three people, only when the
      // band knows a place, only when nobody is hungry enough for it to be a
      // real choice, and only when it has been long enough to be worth the
      // walk. `venerate()` still teaches regard from how they FARED on the
      // trip, so a band that goes while it is doing badly still comes home
      // doubting the ground — the superstition is untouched.
      const holy = this.bestSacred(b);
      if (holy && foodDays > 3.2 && waterDays > 1.2
          && (holy.lastVisit === undefined || t - holy.lastVisit >= VISIT_INTERVAL)) {
        ask('venerate', Math.min(3, Math.max(2, Math.round(pop * 0.08))));
      }
    }

    // trim to the headcount in ask-order, then everyone left keeps the fire —
    // and a band that keeps several fires keeps them ALL. The residual splits
    // into one group per fire (groups of three or more), each mustering by
    // its own fire, so the constellation the sprawl law builds is inhabited
    // rather than one lived-at fire with cold satellites. Measured before
    // this existed (`tools/fireside.ts` on the third century): 5 of 14
    // multi-fire bands' fires had anyone beside them, and two bands'
    // residents stood at NEITHER fire — camp parties hashed their patch
    // around the home point, which the first fire itself walks off when it
    // finds the ground crowded.
    // a party still walking home from the dark keeps walking: it survives
    // the morning replan untouched, and its people are not available to the
    // morning's asks — they are on the road (2026-08-23, the working day)
    const homing = wholeBand
      ? []
      : b.parties.filter((p) => p.activity === 'travel' && p.target);
    const homingSize = homing.reduce((s, p) => s + p.size, 0);
    let left = Math.max(0, pop - homingSize);
    const shares: Array<[BandActivity, number]> = [];
    for (const [a, n] of want) {
      if (left <= 0) break;
      const take = Math.min(n, left);
      shares.push([a, take]);
      left -= take;
    }
    // everyone left keeps the fire — one group per own fire, groups of three
    // or more, the nearer fires filled first
    const fires = this.ownFires(b);
    const campAnchors: Array<{ x: number; z: number }> = [];
    if (left > 0) {
      const groups = Math.max(1, Math.min(fires.length || 1, Math.floor(left / 3)));
      for (let g = 0; g < groups; g++) {
        const size = Math.floor(left / groups) + (g < left % groups ? 1 : 0);
        if (size <= 0) continue;
        shares.push(['camp', size]);
        campAnchors.push(fires[g] ?? { x: b.homeX, z: b.homeZ });
      }
    }

    const old = b.parties;
    const next: Party[] = [...homing];
    let campSeen = 0;
    for (const [activity, size] of shares) {
      // a camp group belongs to a particular fire: keep the party already
      // sitting by that fire if there is one, and never steal a group from
      // another fire — an unmatched group disbands and re-musters at its own
      const anchor = activity === 'camp'
        ? campAnchors[campSeen++] ?? { x: b.homeX, z: b.homeZ }
        : { x: b.homeX, z: b.homeZ };
      const kept = activity === 'camp'
        ? old.find((p) => p.activity === 'camp' && !next.includes(p) &&
            Math.hypot(p.x - anchor.x, p.z - anchor.z) <= 30)
        : old.find((p) => p.activity === activity && !next.includes(p));
      if (kept) {
        kept.size = size;
        next.push(kept);
        continue;
      }
      // a new party musters at the camp and takes its own patch of ground, so
      // several parties read as several groups rather than one clump
      const n = b.nextParty++;
      const a = hash01(b.idHash + n * 71, decisionIndex, this.seed) * Math.PI * 2;
      const r = activity === 'camp' ? 6 + hash01(b.idHash, n, 5) * 10
                                    : 40 + hash01(b.idHash, n, 7) * 90;
      const p: Party = {
        id: `${b.id}-p${n}`, activity, size,
        x: anchor.x + Math.cos(a) * r, z: anchor.z + Math.sin(a) * r,
        walkKey: '', trace: [], movedToday: 0,
        foragedToday: false, gatheredToday: false,
      };
      // a new party musters at the camp and then goes where its work is
      const dest = wholeBand ? b.target : this.partyTarget(b, activity, n, decisionIndex, t);
      if (dest) p.target = { x: dest.x, z: dest.z };
      next.push(p);
    }

    // anyone finished and far afield walks home. This is what closes the loop
    // that wears a trail: a hunt out to the herd and back is two traversals of
    // the same route, and the third one is a path.
    for (const p of next) {
      if (p.target || wholeBand) continue;
      if (Math.hypot(p.x - b.homeX, p.z - b.homeZ) > HOME_RETURN) {
        p.target = { x: b.homeX, z: b.homeZ };
      }
    }
    // A party that is disbanded before the day boundary would take its unbanked
    // wear with it, and wood parties are short-lived — they exist only while
    // wood is short, so they were routinely dropped at a six-hour replan before
    // anything was written. Measured over 240 days: a wood party existed on 11
    // of them and the woodcutting channel was still exactly zero everywhere.
    // Settle the ground before letting the party go.
    for (const old0 of old) {
      if (next.includes(old0)) continue;
      if (!old0.foragedToday && !old0.gatheredToday && old0.movedToday < TRAVELLED_METRES) continue;
      for (const k of stampDailyImpact(this.land, this.pressure, old0.x, old0.z, {
        foraged: old0.foragedToday,
        gatheredWood: old0.gatheredToday,
        stationary: false,
        travelled: old0.movedToday >= TRAVELLED_METRES,
      }, t)) this.dirty.add(k);
    }

    b.parties = next;
    // and now who goes with which — the band decided how many, the people
    // decide themselves by what they are good at (v0.3.2 §6)
    assignPeople(b.people, next, hash3(b.idHash, PERSON_SALT, this.seed), decisionIndex, t);
  }

  /**
   * Where a party of this kind should go. Parties use the world rather than
   * abstractly benefiting from it (v0.3.2 §7): foragers walk to the best
   * forage in reach, hunters toward the herd, water carriers to the water,
   * woodcutters to the stand, builders to the frame. A pure function of the
   * habitat and the wildlife — the same band in the same world sends the same
   * party to the same place.
   */
  private partyTarget(
    b: Band, activity: BandActivity, n: number, decisionIndex: number, t: number,
  ): { x: number; z: number } | undefined {
    const { sx, sz } = toSocial(b.homeX, b.homeZ);
    /** The best neighbouring land cell by some reading of its habitat. */
    const best = (score: (h: SocialHabitat) => number) => {
      let where: { x: number; z: number } | undefined;
      let top = -Infinity;
      for (const nb of socialNeighbors(sx, sz)) {
        const h = this.habitat.at(nb.sx, nb.sz, t);
        if (h.landFraction < 0.3) continue;
        const s = score(h);
        if (s > top) { top = s; where = socialCenter(nb.sx, nb.sz); }
      }
      return where;
    };

    switch (activity) {
      case 'forage': {
        const away = best((h) => h.foragePotential);
        // …and then look at what the land REMEMBERS, which the cell means
        // cannot see. `socialNeighbors` excludes the home cell on purpose —
        // you do not forage where you sleep — but that also means a cast on
        // the camp itself lands in the one place a party can never be sent,
        // and 18 of this island's 27 bands sit within metres of their own
        // cell centre. So the natural act of blessing the camp you are
        // watching reached nobody.
        //
        // Compared on YIELD, not on either number alone: a cell's forage
        // potential times what the ground under it remembers, which is exactly
        // what `forage()` multiplies when the hands get there. The cell mean
        // still does the judging (law 6) — this only adds a place it was never
        // allowed to consider.
        const tended = this.land.bestGrowth(b.homeX, b.homeZ, TENDED_REACH, t);
        if (!tended) return away;
        const yieldAt = (x: number, z: number) => {
          const c = toSocial(x, z);
          const h = this.habitat.at(c.sx, c.sz, t);
          return h.landFraction < 0.3 ? 0 : h.foragePotential * this.bounty(x, z, t);
        };
        return yieldAt(tended.x, tended.z) > (away ? yieldAt(away.x, away.z) : 0)
          ? { x: tended.x, z: tended.z } : away;
      }
      case 'gather_wood': return best((h) => h.woodyBiomass);
      case 'seek_water': return best((h) => h.waterAccess);
      case 'hunt': {
        let where: { x: number; z: number } | undefined;
        let top = -Infinity;
        for (const nb of socialNeighbors(sx, sz)) {
          if (this.habitat.at(nb.sx, nb.sz, t).landFraction < 0.3) continue;
          const heads = this.wildlife.populationAt(nb.sx, nb.sz, 'grazer')
                      + this.wildlife.populationAt(nb.sx, nb.sz, 'small_game');
          if (heads > top) { top = heads; where = socialCenter(nb.sx, nb.sz); }
        }
        return where;
      }
      case 'build': {
        const site = this.construction.siteFor(b.id);
        return site ? { x: site.x, z: site.z } : undefined;
      }
      case 'explore': {
        // a hashed bearing, rotated off the sea until it finds land
        for (let i = 0; i < 6; i++) {
          const a = hash01(b.idHash + n * 61 + i * 13, decisionIndex, this.seed + 31)
                  * Math.PI * 2;
          const ex = b.homeX + Math.cos(a) * 1500, ez = b.homeZ + Math.sin(a) * 1500;
          if (this.habAt(ex, ez, t).landFraction > 0.3) return { x: ex, z: ez };
        }
        return undefined;
      }
      case 'venerate': {
        const holy = this.bestSacred(b);
        return holy ? { x: holy.x, z: holy.z } : undefined;
      }
      case 'investigate': {
        // a fresh patch of the doubt each time it is planned, so a party
        // that finds nothing sweeps elsewhere rather than pacing one spot
        const sign = this.bestAnomaly(b);
        return sign ? this.searchPoint(b, sign, decisionIndex + n) : undefined;
      }
      default: return undefined;   // camp, rest and idle stay where they are
    }
  }

  /** Consumption, starvation, land wear, and memory — at each day boundary. */
  private daily(t: number): void {
    for (const b of this.list) {
      if (b.population <= 0) continue;
      const hab = this.habAt(b.x, b.z, t);

      // settlement bookkeeping first, on pre-consumption stores: a day whose
      // stores stood at the ceiling counts full, even though supper follows
      this.construction.dailyFor(b, t);

      // eat and drink: one person-day per person (spec §10); a band camped by
      // water spent the day drinking from the source, not the skins
      b.reserves.food = Math.max(0, b.reserves.food - b.population);
      if (hab.waterAccess <= NEAR_WATER) {
        b.reserves.water = Math.max(0, b.reserves.water - b.population);
      }

      if (b.reserves.food <= 0) {
        // one event per episode: hunger is a state, not a drumbeat
        if (!b.shortageOpen) {
          b.shortageOpen = true;
          this.events.emit({
            type: 'resource_shortage', time: t, x: b.x, z: b.z,
            radius: 400, magnitude: b.population, salience: 0.5,
            cause: { kind: 'mortal', bandId: b.id },
            data: { resource: 'food' },
          });
        }
        b.population = Math.max(0, b.population * (1 - STARVE_RATE));
        b.cohesion = clamp(b.cohesion - 0.02, 0, 1);
      } else if (b.reserves.food > b.population) {
        // a full day in hand ends the episode; the next one may cry again
        b.shortageOpen = false;
      }

      // thirst: dry skins with no source in reach. The first day is misery,
      // the second begins to kill — faster than hunger ever does.
      if (b.reserves.water <= 0 && hab.waterAccess <= NEAR_WATER) {
        b.waterlessDays += 1;
        if (b.waterlessDays === 2) {
          // once per episode, at the moment thirst turns deadly
          this.events.emit({
            type: 'resource_shortage', time: t, x: b.x, z: b.z,
            radius: 400, magnitude: b.population, salience: 0.5,
            cause: { kind: 'mortal', bandId: b.id },
            data: { resource: 'water' },
          });
        }
        if (b.waterlessDays >= 2) {
          b.population = Math.max(0, b.population * (1 - THIRST_RATE));
        }
      } else {
        b.waterlessDays = 0;
      }

      // the day's wear on the land (spec §17) — a still day is a camp —
      // written to both ledgers: the EnvField the fire reads, the pressure
      // record the historians keep
      // party by party, so each marks the ground it actually worked: the camp
      // packs bare underfoot while the forage patch is merely harvested and
      // the wood stand is cut. One band, several kinds of wear, several places.
      for (const p of b.parties) {
        for (const key of stampDailyImpact(this.land, this.pressure, p.x, p.z, {
          foraged: p.foragedToday,
          gatheredWood: p.gatheredToday,
          stationary: p.movedToday < ARRIVE_RADIUS * 1.5,
          travelled: p.movedToday >= TRAVELLED_METRES,
        }, t)) this.dirty.add(key);
        p.foragedToday = false;
        p.gatheredToday = false;
        p.movedToday = 0;
      }

      // A band that eats well, sleeps under a roof and holds together grows.
      // All three are required, which is what makes a village the reward for
      // tending a valley rather than something that happens on its own.
      // Food and roofs, both required and both measured. Cohesion is
      // deliberately NOT a factor: it sits at a median of 0.22 across a
      // two-year run because only resting restores it and resting rarely wins
      // a scoring round, so gating growth on it would gate growth on a number
      // that is quietly broken. That imbalance is worth fixing on its own
      // terms rather than hiding inside the birth rate.
      const housed = clamp(this.construction.shelterCapacity(b) / Math.max(1, b.population), 0, 1);
      this.settleCohesion(b, housed);
      // a band that has been coming apart for a season, and is big enough
      // that both halves would still be a community, becomes two
      b.frayedDays = b.cohesion <= FISSION_COHESION ? b.frayedDays + 1 : 0;
      if (b.frayedDays >= FISSION_DAYS && b.population >= FISSION_MIN_POP &&
          b.people.length >= FISSION_MIN_POP) {
        this.split(b, t);
      }
      const fed = clamp(
        (b.reserves.food / Math.max(1, b.population) - GROWTH_FOOD_DAYS) / GROWTH_FOOD_SPAN, 0, 1);
      const thriving = fed * housed;
      if (thriving > 0) b.population += b.population * GROWTH_RATE * thriving;

      // …and people die. Ordinary death, by age: not starvation, which is the
      // only thing that has ever reduced a population here and which never
      // fires. This runs *before* reconcile so the roster and the count fall
      // together — kill somebody and drop the population by one, and the
      // reconcile below will not quietly replace them.
      this.reap(b, t);
      this.reconcilePeople(b, t);

      // (the day's shaping — liveADay, crossings, bonds, haunts — moved to
      // the DUSK boundary, 2026-08-23: this boundary is midnight, and under
      // the working day everyone at midnight answers "camp", so a day of
      // hunting was recorded as a night at the fire. `duskLife` in sixHourly
      // reads the parties while they still hold the day's shape.)
      fadeBonds(b.people);
      fadeHaunts(b.people);

      // …and the band slowly becomes the people in it.
      //
      // THE SECOND HALF OF THE CHAIN. Band traits decide behaviour — mobility
      // scales every targeted candidate's distance penalty, and `curiosity`
      // over 0.55 is a hard gate on mustering an explore party at all — but
      // until now they were written at exactly three moments: spawn, fission
      // and merge. So a roster could change character completely and the band
      // would keep deciding as its founders did, until a split generations
      // later finally read the room. That is why "strengthen their curiosity
      // and they range farther" had a variable and still could not happen.
      //
      // The expression is deliberately fission's own, not a second one:
      // `curiosity` is the roster mean and `caution` is one minus the mean
      // courage, exactly as `split()` computes a daughter's character from
      // whoever left. Two copies of that arithmetic would be the fourth
      // hand-mirrored expression this repository has paid for.
      //
      // TRACK is a move-toward and not a snap, and it is small on purpose:
      // the band's character should lag its people, because a band IS its
      // history as much as its roster. `mobility` and `tradition` have no
      // person-level counterpart and are left to fission and merge.
      if (b.people.length > 0) {
        let c = 0, g = 0;
        for (const p of b.people) { c += p.traits.curiosity; g += p.traits.courage; }
        const n = b.people.length;
        b.traits.curiosity = clamp(
          b.traits.curiosity + (c / n - b.traits.curiosity) * BAND_TRACK, 0.05, 0.95);
        b.traits.caution = clamp(
          b.traits.caution + ((1 - g / n) - b.traits.caution) * BAND_TRACK, 0.05, 0.95);
      }

      // places fade slowly (~0.99/day); the ground underfoot stays sharp
      for (const p of b.knownPlaces) p.confidence *= 0.99;
      this.rememberPlace(b, t);
      b.uncertainty = clamp(b.uncertainty * 0.995 + 0.002, 0, 1);
    }

    // world-side upkeep, once per day boundary: ruins crumble, unused paths
    // fade (and may dissolve), and pressure cells decayed to nothing lapse.
    // Who is still alive decides which roofs have anybody left to mend them —
    // a band that died out or walked into another leaves a camp behind just
    // as surely as one that re-homed over the hill.
    this.construction.daily(new Set(
      this.list.filter((b) => b.population > 0).map((b) => b.id)));
    this.pathSys.daily(t);
    this.pressure.sweep(t);
    // and who has been living next to whom, which is how two bands become one
    this.tickContacts(t);
  }

  /**
   * Every row of the table a band would read right now, scored. It builds the
   * identical context the real decision builds, so what it prints is what the
   * band saw, and the simulation never calls it.
   *
   * **It used to claim to write nothing and it did.** `contextFor` falls back
   * to `recordPlace` when a band has no memory of its own home cell, and
   * `recordPlace` pushes a `PlaceMemory` and can evict the faintest one it
   * holds. Called once by a tool that is about to throw the world away, that is
   * invisible; called by a card, every frame, on whatever the player is
   * looking at, it is **law 9 broken by the act of asking** — observation
   * creating history, in the one place the whole design says it must not.
   * `contextFor` takes an `observe` flag now, and this passes it.
   */
  explain(bandId: string, t: number): ReadonlyArray<{ a: BandActivity; s: number }> {
    const b = this.list.find((x) => x.id === bandId);
    if (!b) return [];
    return scoreTable(b, this.contextFor(b, t, Math.floor(t / DECISION_STEP), true),
                      Math.floor(t / DECISION_STEP))
      .map((c) => ({ a: c.a, s: c.s }));
  }

  /**
   * WHY THIS BAND IS DOING WHAT IT IS DOING — the whole reading, for a card.
   *
   * `explain` returns scores, and a score is not an explanation: `1.272` is
   * exactly the kind of number `ui/lives.ts` exists to keep off the screen.
   * What a player needs is the *shape* — what the band is short of, what it
   * can see, what it nearly did instead — and every one of those is already
   * computed by the decision and then thrown away. This hands them over.
   *
   * Three things it does that `explain` cannot, and the third is the one that
   * would otherwise make a card lie:
   *
   *  · **`chose` is the band's real activity, not the table's maximum.**
   *    `decide` short-circuits ahead of the table twice — fire at the door
   *    goes straight to `flee`, and a strictly better remembered place goes
   *    straight to `travel` — so argmax(table) disagrees with the world in
   *    precisely the two most dramatic moments there are. `override` says
   *    which, so the card can explain a decision the table never made.
   *  · **The pressures**, which is what "why" nearly always is: a band forages
   *    because it is hungry, and the table only shows that the hungry rows
   *    won.
   *  · **`runnerUp`**, because *what they nearly did instead* is the single
   *    most legible thing about a choice, and it is free here.
   *
   * A pure read, with `observe` set — see `explain`.
   */
  reading(bandId: string, t: number): BandReading | null {
    const b = this.list.find((x) => x.id === bandId);
    if (!b) return null;
    const i = Math.floor(t / DECISION_STEP);
    const ctx = this.contextFor(b, t, i, true);
    const table = scoreTable(b, ctx, i).map((c) => ({ a: c.a, s: c.s }));
    const ranked = [...table].sort((p, q) => q.s - p.s || p.a.localeCompare(q.a));
    return {
      chose: b.activity,
      // The same two short-circuits `decide` takes, read back off the world
      // rather than recomputed — `migrating` is the band's own flag, and the
      // danger threshold is `decide`'s own 0.5.
      ...(ctx.danger > 0.5 ? { override: 'fire' as const }
        : b.migrating ? { override: 'migrating' as const } : {}),
      table,
      best: ranked[0]?.a ?? b.activity,
      // A guarded-off row scores exactly −1 before the tax, so anything at or
      // under it was never really on the table; the runner-up has to be a row
      // the band could actually have taken.
      ...(ranked[1] && ranked[1].s > -0.999 ? { runnerUp: ranked[1].a } : {}),
      pressures: pressuresOf(b, ctx.here, ctx.danger, ctx.shelterCapacity),
      abundance: ctx.abundance,
      danger: ctx.danger,
      forage: ctx.here.foragePotential,
      woodNeed: ctx.woodNeed,
      // How hard an unexplained sign is pulling — the Omen's own row, and the
      // one thing on this list that is a god's doing rather than the land's.
      drawn: ctx.anomaly ? ctx.anomaly.salience * ctx.anomaly.credibility : 0,
      ...(ctx.sacred ? { regard: ctx.sacred.regard } : {}),
      cohesion: b.cohesion,
      frayedDays: b.frayedDays,
    };
  }

  /**
   * A GOD LEANS ON THE PEOPLE INSIDE A CAST, AND ON WHAT THEY ARE DOING.
   *
   * This is the link the chain was missing. `Simulation.act` reached
   * `pantheon, region, env, anchors, mod, things, dirty, events, fire` and
   * never `bands`, so a cast aimed squarely at a named person degraded in
   * silence to a stamp on the grass at their feet: the card said *curious*,
   * the god pressed on it, and nothing anywhere was different.
   *
   * **Position comes from the party, because a person has none.**
   * `PersonState` carries no coordinates by design — people are spread around
   * their party's anchor — so the footprint test is per party, and everyone
   * out on the same errand is at the same remove from the cast. That is also
   * what makes the rule legible: you are pressing on *a working group*, and
   * the work is what you are able to press on.
   *
   * **Hardest at the centre, weaker outward** — Griffin's standing direction
   * for a channelled verb, applied to lives rather than to plants. A named
   * target takes the full press wherever they stand inside the disc, because
   * naming them is what the aim was for; everyone else falls off with the
   * square of distance. Law 14 still holds: naming the person says where the
   * causal pressure lands and says nothing whatever about the outcome.
   *
   * Determinism: bands in list order, parties in band order, people in roster
   * order, and `press` reads nobody but its own person — so no iteration order
   * can change the result. Nothing is emitted here. The caller owns the event,
   * because the caller is the one that knows whose hand it was, and this file
   * is not allowed to (`test/deity.ts` scans it for the word).
   */
  press(
    x: number, z: number, radius: number,
    sign: 1 | -1, strength: number, now: number,
    targetId?: string,
    /** Whether this god's press reaches what a person lately became (`people.ts` press). */
    reachBecoming = false,
  ): { touched: number; moved: number; named: boolean; became: Array<{ bandId: string; crossing: Crossing }> } {
    let touched = 0, moved = 0, named = false;
    const became: Array<{ bandId: string; crossing: Crossing }> = [];
    if (radius <= 0 || strength <= 0) return { touched, moved, named, became };
    const r2 = radius * radius;

    for (const b of this.list) {
      if (b.population <= 0) continue;
      const activity = new Map<string, BandActivity>();
      for (const p of b.parties) activity.set(p.id, p.activity);
      const where = new Map<string, number>();
      for (const p of b.parties) {
        const dx = p.x - x, dz = p.z - z;
        where.set(p.id, dx * dx + dz * dz);
      }
      // what everybody was before the hand landed, so a crossing can be told
      // from a nudge: the caller emits `person_changed` with the cast as its
      // parent, which is horizon C for a Strengthen laid on a life
      const axesBefore = axesOf(b.people);

      for (const person of b.people) {
        if (!person.partyId) continue;
        const d2 = where.get(person.partyId);
        if (d2 === undefined) continue;
        const isTarget = person.id === targetId;
        if (!isTarget && d2 > r2) continue;
        // full weight for the one you aimed at; the square falloff otherwise
        const falloff = isTarget ? 1 : 1 - d2 / r2;
        const m = press(person, activity.get(person.partyId), sign,
                        strength * falloff, now, reachBecoming);
        if (m > 0) {
          touched += 1;
          moved = Math.max(moved, m);
          // THE ONE YOU AIMED AT, reported separately — and the distinction is
          // not pedantry. Parties cluster at a camp, so a cast aimed at
          // somebody sitting by the fire lands on the sixty-odd people around
          // them who ARE out on work, and `touched` alone would then report a
          // large number for a person who moved not at all. The chrome would
          // say "Dasha takes to it, and those beside them" about somebody the
          // cast could not touch — the exact overstatement law 14 warns
          // against, and it would hide the rule the player most needs to
          // learn.
          if (isTarget) named = true;
        }
      }
      for (const crossing of crossingsSince(b.people, axesBefore, now)) became.push({ bandId: b.id, crossing });
    }
    return { touched, moved, named, became };
  }

  /** One decision for one band; applies it and emits migration events. */
  private decideFor(b: Band, t: number, decisionIndex: number): void {
    const danger = clamp(this.hazards.dangerAt(b.x, b.z, t), 0, 1);
    // mid-migration, keep walking unless the world is on fire
    if (b.migrating && b.target && danger <= 0.5) return;
    this.applyDecision(b, t, decisionIndex, this.contextFor(b, t, decisionIndex));
  }

  /** Everything the scoring reads, gathered from the world at this moment. */
  /**
   * Everything the scoring reads, gathered from the world at this moment.
   *
   * `observe` is the difference between a band deciding and a god watching a
   * band decide. The live path legitimately WRITES — a band with no memory of
   * its own home looks once and writes it down, which is the band learning
   * something. A card asking the same question must not: law 9, and the reason
   * `explain` and `reading` both set it. The only write on the path is the
   * home-memory fallback below, and under `observe` it is read without being
   * kept, which changes the returned context in no way at all.
   */
  private contextFor(
    b: Band, t: number, decisionIndex: number, observe = false,
  ): DecisionContext {
    const danger = clamp(this.hazards.dangerAt(b.x, b.z, t), 0, 1);
    const { sx, sz } = toSocial(b.x, b.z);
    const here = this.habitat.at(sx, sz, t);
    const cc = socialCenter(sx, sz);

    // Squash head counts to appetite: 40 head reads as rich hunting grounds.
    // What matters is the best ground a hunting party could reach, not the
    // ground the camp happens to sit on — the band decides at the fire, but it
    // hunts a day's walk away. Reading only the camp's own cell left a band
    // with an empty valley underfoot permanently blind to the herd next door.
    let heads = this.wildlife.populationAt(sx, sz, 'grazer')
      + this.wildlife.populationAt(sx, sz, 'small_game');
    for (const nb of socialNeighbors(sx, sz)) {
      heads = Math.max(heads, this.wildlife.populationAt(nb.sx, nb.sz, 'grazer')
        + this.wildlife.populationAt(nb.sx, nb.sz, 'small_game'));
    }
    const abundance = 1 - Math.exp(-heads / 40);

    // hunters follow the herds: when a neighbouring cell carries clearly more
    // game, the hunt walks there — and the walk home wears the trail. A hunt
    // already under way keeps its ground: herds shift daily, and a walk that
    // re-aimed every six hours would abandon every trip it started.
    let huntTarget: { x: number; z: number } | undefined;
    if (b.activity === 'hunt' && b.target) {
      huntTarget = { x: b.target.x, z: b.target.z };
    } else {
      let bestHeads = heads * 1.3 + 20;
      for (const nb of socialNeighbors(sx, sz)) {
        const h = this.wildlife.populationAt(nb.sx, nb.sz, 'grazer')
          + this.wildlife.populationAt(nb.sx, nb.sz, 'small_game');
        if (h > bestHeads && this.habitat.at(nb.sx, nb.sz, t).landFraction > 0.3) {
          bestHeads = h;
          huntTarget = socialCenter(nb.sx, nb.sz);
        }
      }
    }

    // water fallback: the best-watered land cell next door, else memory
    let waterTarget: { x: number; z: number } | undefined;
    if (here.waterAccess <= NEAR_WATER) {
      let bestAccess = NEAR_WATER;
      for (const nb of socialNeighbors(sx, sz)) {
        const h = this.habitat.at(nb.sx, nb.sz, t);
        if (h.waterAccess > bestAccess && h.landFraction > 0.2) {
          bestAccess = h.waterAccess;
          waterTarget = socialCenter(nb.sx, nb.sz);
        }
      }
      if (!waterTarget) {
        for (const kp of b.knownPlaces) {
          if (kp.waterEstimate > bestAccess && kp.confidence > 0.3) {
            bestAccess = kp.waterEstimate;
            waterTarget = { x: kp.x, z: kp.z };
          }
        }
      }
    }

    // explore: a hashed bearing per decision index, rotated off the ocean
    let exploreTarget: { x: number; z: number } | undefined;
    for (let i = 0; i < 6 && !exploreTarget; i++) {
      const a = hash01(b.idHash + i * 61, decisionIndex, this.seed + 31) * Math.PI * 2;
      const ex = b.x + Math.cos(a) * 1500;
      const ez = b.z + Math.sin(a) * 1500;
      if (this.habAt(ex, ez, t).landFraction > 0.3) exploreTarget = { x: ex, z: ez };
    }

    // flee: of eight compass bearings, the one where the danger is least
    let fleeTarget: { x: number; z: number } | undefined;
    if (danger > 0.5) {
      let least = Infinity;
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        const fx = b.x + Math.cos(a) * 1200;
        const fz = b.z + Math.sin(a) * 1200;
        if (this.habAt(fx, fz, t).landFraction < 0.3) continue;
        const d = this.hazards.dangerAt(fx, fz, t);
        if (d < least) { least = d; fleeTarget = { x: fx, z: fz }; }
      }
    }

    // home value, as the band remembers it — no omniscience: the band judges
    // home by its own PlaceMemory (refreshed whenever it stands there), so a
    // band long afield weighs a stale picture of home, and that is intended.
    // A band with no memory of home at all looks once and writes it down.
    const hs = toSocial(b.homeX, b.homeZ);
    const hc = socialCenter(hs.sx, hs.sz);
    const homeMem = b.knownPlaces.find((p) => p.x === hc.x && p.z === hc.z)
      ?? (observe ? this.placeReading(b.homeX, b.homeZ, t)
                  : this.recordPlace(b, b.homeX, b.homeZ, t));
    const homeValue = placeValue(homeMem);

    // construction context: the open project, the wood it still wants beyond
    // what the band carries, and the comfort of a hearth burning within reach
    const site = this.construction.siteFor(b.id);
    const siteShort = site
      ? site.required.wood - site.delivered.wood > b.reserves.wood
      : false;
    const woodNeed = siteShort
      ? 1
      : clamp((10 - b.reserves.wood) / 10, 0, 1) * 0.5;
    const hearth = this.construction.hearthNear(b.x, b.z);
    const comfort = hearth ? 0.06 * hearth.condition : 0;

    const ctx: DecisionContext = {
      seed: this.seed,
      idHash: b.idHash,
      here,
      danger,
      abundance,
      familiar: b.knownPlaces.find((p) => p.x === cc.x && p.z === cc.z)?.confidence ?? 0,
      homeValue,
      woodNeed,
      comfort,
      shelterCapacity: this.construction.shelterCapacity(b),
    };
    // the sign most worth walking to, and a point inside its doubt to search
    const sign = this.bestAnomaly(b);
    if (sign) {
      ctx.anomaly = {
        salience: sign.salience, credibility: sign.credibility,
        specificity: sign.specificity,
      };
      ctx.anomalyTarget = this.searchPoint(b, sign, decisionIndex);
    }
    // and the place it would most like to stand at again
    const holy = this.bestSacred(b);
    if (holy) {
      ctx.sacred = {
        regard: holy.regard, visits: holy.visits,
        since: holy.lastVisit === undefined ? Infinity : t - holy.lastVisit,
      };
      ctx.sacredTarget = { x: holy.x, z: holy.z };
    }
    if (site) ctx.buildTarget = { x: site.x, z: site.z };
    if (waterTarget) ctx.waterTarget = waterTarget;
    if (exploreTarget) ctx.exploreTarget = exploreTarget;
    if (fleeTarget) ctx.fleeTarget = fleeTarget;
    if (huntTarget) ctx.huntTarget = huntTarget;
    return ctx;
  }

  /** Take the decision the context implies, and live with it. */
  private applyDecision(
    b: Band, t: number, decisionIndex: number, ctx: DecisionContext,
  ): void {
    const d = decide(b, ctx, decisionIndex);
    if (d.migrateTo) {
      // re-home at the decision (spec §13); arrival will confirm it
      b.homeX = d.migrateTo.x;
      b.homeZ = d.migrateTo.z;
      b.migrating = true;
      // structures left behind fall unowned; one camp_abandoned marks the leaving
      this.construction.onRehome(b, t);
      this.events.emit({
        type: 'band_migrated', time: t, x: d.migrateTo.x, z: d.migrateTo.z,
        radius: 500, magnitude: b.population, salience: 0.45,
        cause: { kind: 'mortal', bandId: b.id },
      });
    } else if (d.activity !== 'travel') {
      // any non-travel decision abandons a migration still under way
      b.migrating = false;
    }
    b.activity = d.activity;
    if (d.target) b.target = { x: d.target.x, z: d.target.z };
    else if (d.activity !== 'travel' && d.activity !== 'flee') {
      // an abandoned walk is no traversal: the band's own destination lapses,
      // and each party keeps or drops its own in planParties
      delete b.target;
      // the camp pulls its people home: a quiet spell far afield becomes the
      // walk back — the other half of every trail a hunt wears outward
      if ((d.activity === 'rest' || d.activity === 'idle') &&
          Math.hypot(b.x - b.homeX, b.z - b.homeZ) > 200) {
        b.target = { x: b.homeX, z: b.homeZ };
      }
    }
  }

  /** Add or refresh the memory of the cell the band is standing in. */
  private rememberPlace(b: Band, t: number): void {
    this.recordPlace(b, b.x, b.z, t);
  }

  /**
   * Look at the cell under (wx, wz) and write it into the band's places —
   * add or refresh, evicting the faintest memory when the book is full.
   * Returns the (live) stored memory.
   */
  /** What a band standing here WOULD write down. Side-effect free, on
   *  purpose: `contextFor(observe)` needs the reading without the writing. */
  private placeReading(wx: number, wz: number, t: number): PlaceMemory {
    const { sx, sz } = toSocial(wx, wz);
    const c = socialCenter(sx, sz);
    const hab = this.habitat.at(sx, sz, t);
    return {
      x: c.x, z: c.z,
      lastVisited: t,
      foodEstimate: hab.foragePotential,
      waterEstimate: hab.waterAccess,
      dangerEstimate: clamp(this.hazards.dangerAt(c.x, c.z, t) * 0.7 + hab.fireRisk * 0.4, 0, 1),
      confidence: 1,
    };
  }

  private recordPlace(b: Band, wx: number, wz: number, t: number): PlaceMemory {
    const fresh = this.placeReading(wx, wz, t);
    const c = { x: fresh.x, z: fresh.z };
    const prev = b.knownPlaces.find((p) => p.x === c.x && p.z === c.z);
    if (prev) {
      Object.assign(prev, fresh);
      return prev;
    }
    b.knownPlaces.push(fresh);
    if (b.knownPlaces.length > MAX_PLACES) {
      // forget the faintest place, not the oldest — old but vivid beats vague
      let lo = 0;
      for (let i = 1; i < b.knownPlaces.length; i++) {
        if (b.knownPlaces[i].confidence < b.knownPlaces[lo].confidence) lo = i;
      }
      b.knownPlaces.splice(lo, 1);
    }
    return fresh;
  }

  /**
   * One event reaches the particular people who could have seen it — called
   * by the simulation synchronously at emission, so memory forms the same
   * tick the world moved (spec Phase A). An event is witnessed once, at its
   * moment, or not at all.
   *
   * A miracle belongs to people now, not to a point average (v0.3.2). Each
   * person stands where their party stands, and is graded by that ground:
   * under the event they are a direct witness, within sight they observed
   * it, within earshot they heard it, and beyond that they hold *nothing* —
   * not yet informed is a real state, and it is most of the band most of
   * the time. The band's own communal record forms here only when someone
   * who saw it was at the hearth (the community was effectively present) or
   * when the band itself was the cause; otherwise the story physically
   * travels home in the heads of the party and lands in deliverNews().
   *
   * Directly witnessing the divine is history singling a person out:
   * importance rises, and with it their place in the follow order.
   */
  witness(e: EventRecord): void {
    if (!worthRemembering(e)) return;
    const reach = e.radius +
      (e.type === 'divine_omen' ? OMEN_RANGE : WITNESS_RANGE);
    for (const b of this.list) {
      if (b.population <= 0) continue;

      // every person is graded from their own party's ground
      let sawFromHome = 0;   // strongest personal importance held at the hearth
      const weight = memorability(e);
      for (const person of b.people) {
        const party = b.parties.find((q) => q.id === person.partyId);
        const px = party?.x ?? b.x, pz = party?.z ?? b.z;
        const dist = Math.hypot(e.x - px, e.z - pz);
        if (dist > reach) continue;
        const kind: WitnessKind = dist <= e.radius + DIRECT_MARGIN ? 'direct'
          : dist <= e.radius + OBSERVED_RANGE ? 'observed' : 'heard';
        const importance = clamp(e.salience * KIND_WEIGHT[kind] * weight, 0, 1);
        const home = Math.hypot(px - b.homeX, pz - b.homeZ) <= HOME_RETURN;
        this.remember(person, {
          eventId: e.id, time: e.time, kind, importance, x: e.x, z: e.z,
          // a sight from the hearth is communal the moment it happens —
          // there is nobody to carry it home to
          told: home,
          // how vast it seemed is most of how vague it is: the account
          // carried home has to carry that too
          ...(e.type === 'divine_omen' ? { radius: e.radius } : {}),
        });
        if (home) sawFromHome = Math.max(sawFromHome, importance);
        // the divine, seen up close, marks a life (v0.3.2 §importance)
        if (e.cause.kind === 'divine' && kind !== 'heard') {
          person.importance = clamp(
            person.importance + e.salience * (kind === 'direct' ? 0.15 : 0.05), 0, 1);
        }
      }

      // the communal record: the band's own doings are communal knowledge by
      // definition, and anything seen from the hearth was seen together
      const own = e.cause.kind === 'mortal' && e.cause.bandId === b.id;
      if (own) {
        const dist = Math.hypot(e.x - b.x, e.z - b.z);
        if (dist <= reach) {
          const falloff = dist <= e.radius
            ? 1 : 1 - 0.7 * ((dist - e.radius) / WITNESS_RANGE);
          this.bandRemember(b, e.id, e.time, clamp(e.salience * falloff * 1.25, 0, 1),
                            e.x, e.z, GRIM.has(e.type));
        }
      } else if (sawFromHome > 0) {
        this.bandRemember(b, e.id, e.time, sawFromHome, e.x, e.z, GRIM.has(e.type));
      }
      // a sign becomes something to explain the moment the community holds
      // it — which is here if the fire saw it, and otherwise not until the
      // party that did walks home (see deliverNews)
      if (e.type === 'divine_omen' && b.memories.some((m) => m.eventId === e.id)) {
        this.noteAnomaly(b, {
          eventId: e.id, time: e.time, x: e.x, z: e.z,
          radius: e.radius, salience: e.salience,
        }, e.time);
      }
    }
  }

  /**
   * Anything a walking party stumbles across (v0.3.2 §Anchor). An Anchor
   * grants nobody: this is the only way a band ever learns one exists, and
   * it is entirely a matter of having walked near it.
   */
  private noticeAnchors(b: Band, p: Party, t: number): void {
    for (const a of this.anchors.near(p.x, p.z, NOTICE_RANGE)) {
      if (b.sacred.some((s) => s.anchorId === a.id)) continue;
      this.anchors.discover(a, b.id, t);
      b.sacred.push({
        anchorId: a.id, x: a.x, z: a.z,
        // found, not yet believed in: enough to be worth one trip back
        regard: 0.25,
        visits: 0, firstSeen: t,
      });
      this.recordPlace(b, a.x, a.z, t);
    }
  }

  /**
   * Standing at a place that matters (v0.3.2 §Anchor).
   *
   * This is where the sacred landscape is actually built — by mortals, with
   * their feet — and where regard is taught. Regard is superstition done
   * honestly: the place is credited with how the band happens to be faring
   * when it comes. A fed, whole community goes home surer of the ground it
   * stood on; a hungry one goes home doubting it. The god's own act is not
   * an input to this at any point, which is the design law written as
   * arithmetic — the interpretation escapes you.
   */
  private venerate(b: Band, s: SacredMemory, t: number): void {
    const a = this.anchors.all().find((x) => x.id === s.anchorId);
    if (!a) return;
    s.visits += 1;
    s.lastVisit = t;
    this.anchors.visit(a, b.id, t);
    const foodDays = b.reserves.food / Math.max(1, b.population);
    // faring well is the whole evidence: full stores and a band that holds
    const faring = clamp(foodDays / 6, 0, 1) * 0.6 + b.cohesion * 0.4;
    s.regard = clamp(s.regard + (faring - 0.45) * 0.22, 0, 1);
  }

  /** The place this band would most like to stand at again. */
  private bestSacred(b: Band): SacredMemory | undefined {
    let best: SacredMemory | undefined;
    let top = 0;
    for (const s of b.sacred) {
      if (s.regard > top) { top = s.regard; best = s; }
    }
    return best;
  }

  /**
   * What holds a band together, once a day (v0.4).
   *
   * Cohesion was a ratchet. Measured over five world years it started at the
   * 0.7 a band is founded with, collapsed inside the first year and then sat
   * at a median of 0.229 forever, 94% of days below 0.30 — the same number
   * for a thriving band as for a starving one, because the only thing that
   * ever restored it was resting and resting is 8% of party-hours. A
   * quantity that does not discriminate cannot carry a decision, which is
   * why it was deliberately kept out of the growth gate and why band fission
   * could not be built on it.
   *
   * So it tracks a target instead. What a community's togetherness is worth
   * depends on whether it eats, whether it has roofs, how many of them there
   * are, and how far apart they are working — and it moves toward that over
   * a fortnight rather than being spent and never earned. The incremental
   * costs elsewhere (a day's walking, a shortage) stay as texture; this is
   * what gives the number a floor and a ceiling that mean something.
   */
  private settleCohesion(b: Band, housed: number): void {
    const foodDays = b.reserves.food / Math.max(1, b.population);
    const fed = clamp(foodDays / COHESION_FED_DAYS, 0, 1);
    // A bigger community is harder to be one community, and there is no
    // ceiling on that — clamped at 1 it made a band of seventy and a band of
    // two hundred read exactly alike, so growth stopped mattering to
    // togetherness at the very size where it should matter most. Uncapped,
    // this is what eventually makes a band too large to be a band.
    const crowd = Math.max(0,
      (b.population - COHESION_EASY_SIZE) / COHESION_SIZE_SPAN);
    // and one whose parties are scattered across the country is less of one
    let spread = 0, souls = 0;
    for (const p of b.parties) {
      spread += Math.hypot(p.x - b.homeX, p.z - b.homeZ) * p.size;
      souls += p.size;
    }
    const apart = souls > 0
      ? clamp((spread / souls) / COHESION_SPREAD_METRES, 0, 1) : 0;
    const target = clamp(
      0.22 + fed * 0.46 + housed * 0.24 - crowd * 0.75 - apart * 0.18,
      0.05, 0.95);
    b.cohesion = clamp(b.cohesion + (target - b.cohesion) * COHESION_TRACK, 0, 1);
  }

  /**
   * A community comes apart (Design Expansion II §48).
   *
   * Splitting is the engine that turns one band into a history: two
   * descendant groups that inherit the same past and then stop sharing it.
   * It is not a failure state — the parent is usually fine afterwards, and
   * often better, because the thing that was fraying it was its own size.
   *
   * Who leaves is the interesting part, and it is not random. The people who
   * go are the ones least bound to the others, sorted by sociability, and
   * that is what makes the daughter band a different *kind* of community
   * rather than half of the same one: its traits are read off the people who
   * actually walked, so a band that sheds its restless produces a restless
   * band. Cultural divergence starts here, from a real cause.
   *
   * They take their own memories with them, because those live in their
   * heads (see PersonState.memories), and a copy of what the community knew
   * — places, stories, the signs it holds, the ground it holds sacred. From
   * the next day those two records diverge and never rejoin.
   */
  private split(b: Band, t: number): void {
    const order = [...b.people].sort((x, y) =>
      x.traits.sociability - y.traits.sociability ||
      (x.id < y.id ? -1 : x.id > y.id ? 1 : 0));
    const leaving = order.slice(0, Math.floor(order.length * FISSION_SHARE));
    if (leaving.length < FISSION_MIN_HALF) return;
    const staying = new Set(order.slice(leaving.length).map((p) => p.id));

    /**
     * Where they go: the best ground they can find at a distance, judged the
     * way a band judges anywhere it might settle.
     *
     * A hashed bearing was not enough. The daughter landed on whatever
     * happened to be 4 km along it, which was usually much poorer than the
     * camp it had left — and since walking out re-taught it that camp on the
     * way past, migration correctly turned it round and it re-homed on top of
     * the band it had just left, six hours after parting. People who leave go
     * somewhere they think is worth leaving for.
     */
    const ang0 = hash01(b.idHash, Math.round(t / DAY_SECONDS), this.seed ^ 0x5b1) * Math.PI * 2;
    let hx = b.homeX + Math.cos(ang0) * FISSION_DISTANCE;
    let hz = b.homeZ + Math.sin(ang0) * FISSION_DISTANCE;
    let best = -Infinity;
    for (let ring = 0; ring < 3; ring++) {
      const dist = FISSION_DISTANCE * (1 + ring * 0.45);
      for (let i = 0; i < 12; i++) {
        const a2 = ang0 + (i / 12) * Math.PI * 2;
        const cx = b.homeX + Math.cos(a2) * dist;
        const cz = b.homeZ + Math.sin(a2) * dist;
        const h = this.habAt(cx, cz, t);
        if (h.landFraction < 0.6) continue;
        // the nearer of two equally good places wins: leaving is not exile
        const s = h.settlementSuitability - (dist / FISSION_DISTANCE) * 0.04;
        if (s > best) { best = s; hx = cx; hz = cz; }
      }
    }

    // The daughter is the same people. §50 is explicit that a band may split
    // and both halves remain one culture for generations — what separates
    // them is time and distance, not the parting itself.
    const child = this.spawn(hx, hz, leaving.length, t,
                             this.cultures.get(b.cultureId)) as Band;
    child.parentId = b.id;
    // the daughter's character is the character of whoever left
    const mean = (f: (p: PersonState) => number) =>
      leaving.reduce((s, p) => s + f(p), 0) / Math.max(1, leaving.length);
    child.traits.curiosity = clamp(mean((p) => p.traits.curiosity), 0.05, 0.95);
    child.traits.caution = clamp(1 - mean((p) => p.traits.courage), 0.05, 0.95);
    // people who leave are, by having left, less bound to how it was done
    child.traits.mobility = clamp(b.traits.mobility + 0.15, 0.05, 0.95);
    child.traits.tradition = clamp(b.traits.tradition - 0.2, 0.05, 0.95);
    // the past is shared; from tomorrow it is two pasts
    // `shown` and `bonds` are copied rather than shared. To be exact about
    // why, because `cloneBand`'s version of this note describes a live
    // corruption and this one does not: here the parent DROPS the leavers'
    // original objects a few lines down (`b.people = b.people.filter(…)`), so
    // sharing them was harmless today. It is made unconditional so that it
    // stays harmless if fission ever keeps a leaver on both rosters — which
    // fusion already does in the other direction.
    //
    // `bonds` is deliberately NOT pruned to the people who left. A bond to
    // somebody who stayed behind is the most interesting thing a split can
    // produce: the band came apart and somebody still keeps turning to look
    // back. It fades on its own if they never work together again, which is
    // the honest way for it to end.
    child.people = leaving.map((p) => ({
      ...p, traits: { ...p.traits }, aptitudes: { ...p.aptitudes },
      memories: p.memories.map((m) => ({ ...m })),
      ...(p.shown ? { shown: { ...p.shown } } : {}),
      ...(p.bonds ? { bonds: p.bonds.map((x) => ({ ...x })) } : {}),
      ...(p.lost ? { lost: p.lost.map((x) => ({ ...x })) } : {}),
      ...(p.became ? { became: p.became.map((x) => ({ ...x })) } : {}),
      ...(p.born ? { born: { ...p.born } } : {}),
      ...(p.haunts ? { haunts: p.haunts.map((x) => ({ ...x })) } : {}),
    }));
    for (const p of child.people) delete p.partyId;
    child.nextPerson = b.nextPerson;
    // Everything the parent knew about the world — except the parent's own
    // camp. They inherited the memory of it, migration read it as the best
    // place they knew, and the daughter walked straight back and re-homed on
    // top of the band it had just left, on the first decision after parting.
    // Leaving is leaving: that ground belongs to the others now.
    child.knownPlaces = b.knownPlaces
      .filter((k) => Math.hypot(k.x - b.homeX, k.z - b.homeZ) > FISSION_DISOWN)
      .map((k) => ({ ...k }));
    child.memories = b.memories.map((m) => ({ ...m }));
    child.anomalies = b.anomalies.map((a) => ({ ...a }));
    child.sacred = b.sacred.map((s) => ({ ...s }));
    child.omenTrust = b.omenTrust;
    child.reserves.food = leaving.length * 2;
    child.parties = [{
      id: `${child.id}-p0`, activity: 'travel', size: leaving.length,
      x: b.homeX, z: b.homeZ, target: { x: hx, z: hz },
      walkKey: '', trace: [], movedToday: 0,
      foragedToday: false, gatheredToday: false,
    }];
    child.nextParty = 1;
    child.activity = 'travel';
    child.x = b.homeX; child.z = b.homeZ;
    child.frayedDays = 0;
    child.cohesion = 0.6;

    // and the parent is smaller, and relieved of exactly what was straining it
    b.people = b.people.filter((p) => staying.has(p.id));
    b.population = Math.max(1, b.population - leaving.length);
    b.reserves.food = Math.max(0, b.reserves.food - child.reserves.food);
    b.frayedDays = 0;
    b.cohesion = 0.6;
    this.planParties(b, Math.floor(t / DECISION_STEP), t);

    this.events.emit({
      type: 'band_split', time: t, x: b.homeX, z: b.homeZ,
      radius: 600, magnitude: leaving.length, salience: 0.7,
      cause: { kind: 'mortal', bandId: b.id },
      data: { left: leaving.length, stayed: b.people.length, to: child.id },
    });
  }

  /**
   * Who has been near whom, counted in days (§48 fusion).
   *
   * Repetition becomes history here as it does everywhere else in this
   * world: two bands do not become one because they are standing close
   * together today, they become one because they have been in each other's
   * country for two months. Drift apart and the acquaintance fades.
   */
  private tickContacts(t: number): void {
    const live = this.list.filter((b) => b.population > 0.5);
    const met = new Set<string>();
    for (let i = 0; i < live.length; i++) {
      for (let j = i + 1; j < live.length; j++) {
        const a = live[i], b = live[j];
        if (Math.hypot(a.homeX - b.homeX, a.homeZ - b.homeZ) > CONTACT_RANGE) continue;
        const key = a.id < b.id ? `${a.id}|${b.id}` : `${b.id}|${a.id}`;
        met.add(key);
        this.contacts.set(key, (this.contacts.get(key) ?? 0) + 1);
      }
    }
    for (const [key, days] of [...this.contacts]) {
      if (met.has(key)) continue;
      const left = days - CONTACT_DECAY;
      if (left <= 0) this.contacts.delete(key);
      else this.contacts.set(key, left);
    }
    // and then the ones who have known each other long enough
    // Culture divergence (§50). A band that has gone a generation without
    // standing near anyone of its own people stops being of them. This is the
    // thing band traits could not carry: they drift by a fixed step into a
    // clamp floor, so the spread across two dozen bands stalled at exactly
    // 0.20 and never widened again. A culture has no floor and its own clock,
    // so a branch that leaves keeps leaving, for as long as the world runs.
    /**
     * Where each people's centre of gravity is — population-weighted, so a
     * hundred souls in one valley outweigh a dozen on the far shore.
     */
    const centre = new Map<string, { x: number; z: number; w: number }>();
    for (const b of live) {
      if (!b.cultureId) continue;
      const c = centre.get(b.cultureId) ?? { x: 0, z: 0, w: 0 };
      c.x += b.homeX * b.population;
      c.z += b.homeZ * b.population;
      c.w += b.population;
      centre.set(b.cultureId, c);
    }

    for (const b of live) {
      const siblings = live.filter((o) => o !== b && o.cultureId === b.cultureId);
      /**
       * Not isolation — *distance from your own people*. Requiring a band to
       * have no kin within a few kilometres never fired once in sixty years:
       * on an island this size everyone always has a neighbour, so twenty-three
       * bands stayed one people forever and §50 was dead content. Real peoples
       * do not need to be cut off to drift apart, they need to be far, and the
       * far end of a spread-out people stops being the same people as the
       * near end. That is a dialect continuum breaking, and it is what makes
       * this reachable on ground anyone will actually play on.
       */
      const c = b.cultureId ? centre.get(b.cultureId) : undefined;
      const far = c && c.w > 0 &&
        Math.hypot(b.homeX - c.x / c.w, b.homeZ - c.z / c.w) > CULTURE_REACH;
      // Apartness ACCUMULATES for anyone far from their people's centre and
      // DECAYS — never zeroes — when regathered (2026-08-23). It used to
      // hard-reset whenever the band happened to be the moment's main body,
      // and that made divergence hang on twenty UNBROKEN years of being the
      // smaller half: measured on a two-band split with pinned food, the
      // halves grew in lockstep (55 vs 56 souls, 48 vs 48), the main-body
      // role flapped roughly yearly, and a pair fifteen kilometres apart
      // stayed one people for twenty-six years with the counter dying at
      // 1,825 days again and again — the attendance-register trap, third
      // sighting (bereavedBy and the wood parties were the first two).
      b.cultureApart = far && siblings.length > 0
        ? b.cultureApart + 1
        : Math.max(0, b.cultureApart - CULTURE_REGATHER);
      if (b.cultureApart >= CULTURE_APART_DAYS) {
        // The main body keeps the name; it is the offshoot that becomes
        // something else. Judged AT THE CROSSING, not daily: the moment's
        // larger half holds the name and its counter simply waits at the
        // line — one flap of the census later, the smaller half crosses.
        const isMainBody = !siblings.some((o) => o.population > b.population ||
          (o.population === b.population && o.id < b.id));
        if (isMainBody) {
          b.cultureApart = CULTURE_APART_DAYS;
          continue;
        }
        const from = this.cultures.get(b.cultureId);
        const born = this.cultures.found(t, from);
        b.cultureId = born.id;
        b.cultureApart = 0;
        this.events.emit({
          type: 'culture_diverged', time: t, x: b.homeX, z: b.homeZ,
          radius: 700, magnitude: b.population, salience: 0.55,
          cause: { kind: 'mortal', bandId: b.id },
          data: { people: born.name, from: from?.name ?? 'unknown' },
        });
      }
    }

    for (const [key, days] of [...this.contacts]) {
      if (days < FUSION_DAYS) continue;
      const [ai, bi] = key.split('|');
      const a = this.list.find((x) => x.id === ai);
      const b = this.list.find((x) => x.id === bi);
      if (!a || !b || a.population <= 0.5 || b.population <= 0.5) continue;
      if (this.tryMerge(a, b, t)) this.contacts.delete(key);
    }
  }

  /**
   * Two bands become one (§48). Nobody dies in a merger: the smaller band's
   * people walk into the larger one carrying their own names and their own
   * memories, which is the whole reason people are persistent entities.
   *
   * It only happens when it would actually help — one of them is too small
   * to hold — and only when the result stays under the size that fission
   * takes apart again, so the two engines do not spend the centuries
   * undoing each other.
   */
  private tryMerge(a: Band, b: Band, t: number): boolean {
    const combined = a.population + b.population;
    if (combined > FUSION_MAX_COMBINED) return false;
    if (Math.min(a.population, b.population) > FUSION_WEAK) return false;
    // still the same kind of people? four traits, mean absolute difference
    const d = (Math.abs(a.traits.curiosity - b.traits.curiosity) +
               Math.abs(a.traits.caution - b.traits.caution) +
               Math.abs(a.traits.mobility - b.traits.mobility) +
               Math.abs(a.traits.tradition - b.traits.tradition)) / 4;
    if (1 - d < FUSION_KINSHIP) return false;

    // the larger takes the smaller in; ties break on id so replay is exact
    const [host, joining] = a.population > b.population ? [a, b]
      : b.population > a.population ? [b, a]
      : a.id < b.id ? [a, b] : [b, a];
    const wh = host.population, wj = joining.population, tot = Math.max(1e-9, wh + wj);
    const blend = (f: (x: Band) => number) => (f(host) * wh + f(joining) * wj) / tot;
    host.traits.curiosity = clamp(blend((x) => x.traits.curiosity), 0.05, 0.95);
    host.traits.caution = clamp(blend((x) => x.traits.caution), 0.05, 0.95);
    host.traits.mobility = clamp(blend((x) => x.traits.mobility), 0.05, 0.95);
    host.traits.tradition = clamp(blend((x) => x.traits.tradition), 0.05, 0.95);
    host.omenTrust = clamp(blend((x) => x.omenTrust), 0.05, 1);

    for (const p of joining.people) {
      delete p.partyId;
      host.people.push(p);
    }
    host.population = combined;
    host.reserves.food += joining.reserves.food;
    host.reserves.water += joining.reserves.water;
    host.reserves.wood += joining.reserves.wood;
    // What the newcomers knew, the community now knows — as its OWN copy.
    //
    // These were pushed by reference, and the joining band keeps its records
    // (below: it is not deleted, because a community that ended is history).
    // So the two bands shared one set of objects, and the host's daily
    // `confidence *= 0.99` silently aged the dead band's memory too. A save
    // deep-copies them apart — `cloneBand` maps every place — so the aliasing
    // ended at the first reload and the dead band's records froze there.
    // That made a reloaded world provably not the world that had not
    // reloaded, which is the one thing persistence may not do. `split()`
    // eight hundred lines up already copies; this now matches it.
    for (const k of joining.knownPlaces) {
      if (!host.knownPlaces.some((q) => q.x === k.x && q.z === k.z)) {
        host.knownPlaces.push({ ...k });
      }
    }
    for (const m of joining.memories) {
      if (!host.memories.some((q) => q.eventId === m.eventId)) host.memories.push({ ...m });
    }
    for (const s of joining.sacred) {
      if (!host.sacred.some((q) => q.anchorId === s.anchorId)) host.sacred.push({ ...s });
    }
    for (const an of joining.anomalies) {
      if (!host.anomalies.some((q) => q.eventId === an.eventId)) host.anomalies.push({ ...an });
    }
    while (host.knownPlaces.length > MAX_PLACES) host.knownPlaces.pop();
    while (host.memories.length > MAX_MEMORIES) host.memories.pop();
    // arriving is unsettling, and being arrived at is too
    host.cohesion = clamp(host.cohesion * 0.85, 0, 1);
    host.frayedDays = 0;
    this.planParties(host, Math.floor(t / DECISION_STEP), t);

    // the joining band ceases to be a band. It is not deleted — a community
    // that ended is history, and its structures stand where it left them.
    joining.population = 0;
    joining.people = [];
    joining.parties = [];
    this.construction.onRehome(joining, t);

    this.events.emit({
      type: 'band_merged', time: t, x: host.homeX, z: host.homeZ,
      radius: 500, magnitude: wj, salience: 0.6,
      cause: { kind: 'mortal', bandId: host.id },
      data: { joined: joining.id, souls: Math.round(wj) },
    });
    return true;
  }

  /** The unexplained sign this band would most like an answer to. */
  private bestAnomaly(b: Band): AnomalyMemory | undefined {
    let best: AnomalyMemory | undefined;
    let bestWorth = 0;
    for (const a of b.anomalies) {
      // A sign that has been walked to is not a question any more, whatever
      // the answer was. What it pointed at is a *place* now — written into
      // known ground, where foraging and migration can weigh it like any
      // other — and the band goes back to it for what is there rather than
      // for what it might mean.
      if (a.visitedAt !== undefined) continue;
      const worth = a.salience * a.credibility;
      if (worth > bestWorth) { bestWorth = worth; best = a; }
    }
    return best;
  }

  /**
   * Somewhere inside the doubt to go and look at.
   *
   * Never the believed point itself: a sign is an area, and a band that
   * walked to one exact coordinate would have been given a waypoint by a
   * god, which is the one thing Omen must never be. The point is redrawn
   * per decision, so a party that finds nothing searches somewhere else
   * next time — and a vague sign is a genuinely large country to sweep.
   */
  private searchPoint(
    b: Band, a: AnomalyMemory, decisionIndex: number,
  ): { x: number; z: number } {
    const ang = hash01(b.idHash + 907, decisionIndex, this.seed) * Math.PI * 2;
    const r = a.uncertaintyRadius *
      Math.sqrt(hash01(decisionIndex, b.idHash + 907, this.seed ^ 0x2b1));
    return { x: a.x + Math.cos(ang) * r, z: a.z + Math.sin(ang) * r };
  }

  /**
   * Somebody went and looked (v0.3.2 §Omen — "meaning is taught by
   * outcomes"). What is actually standing on that ground is what the sign
   * turns out to have meant: water or good forage makes it guidance, fire
   * or a dangerous place makes it warning, and bare hillside makes it
   * nothing at all. Only the last of those costs the god anything, and it
   * costs the *practice* of heeding signs, not just this one — which is how
   * a congregation is lost.
   *
   * The band emits its own event for having gone, so the trip is a thing
   * that happened in the world: witnessed, remembered, and tellable.
   */
  private answerAnomaly(
    b: Band, a: AnomalyMemory, party: Party, x: number, z: number, t: number,
  ): void {
    const hab = this.habAt(x, z, t);
    const danger = this.hazards.dangerAt(x, z, t);
    // A standing stone is not an empty hillside. If the sign led them to a
    // place a god has bound itself to, that is the most answered a sign can
    // be — and it is how an Anchor out where nobody walks is ever found at
    // all: measured, a stone more than a kilometre off mortal routes is
    // never stumbled upon, so the informational verb is what carries them
    // there. The two features need each other.
    // a searching party finds what a passing one would have missed — and
    // finding it is what the sign turns out to have meant
    const found0 = this.anchors.near(x, z, SEARCH_RANGE);
    for (const anchor of found0) {
      if (this.anchors.discover(anchor, b.id, t)) {
        b.sacred.push({
          anchorId: anchor.id, x: anchor.x, z: anchor.z,
          regard: 0.25, visits: 0, firstSeen: t,
        });
        this.recordPlace(b, anchor.x, anchor.z, t);
      }
    }
    const stone = found0.length > 0;
    const found: OmenMeaning =
      stone ? 'guidance'
      : danger > 0.35 || hab.fireRisk > 0.7 ? 'warning'
      : hab.waterAccess > NEAR_WATER || hab.foragePotential > 0.55 ? 'guidance'
      : 'empty';
    a.meaning = found;
    a.visitedAt = t;
    // trust is in signs as a practice, and it is what the next one inherits
    const shift = found === 'empty' ? -0.18 : stone ? 0.25 : 0.12;
    b.omenTrust = clamp(b.omenTrust + shift, 0.05, 1);
    a.credibility = found === 'empty' ? 0 : clamp(a.credibility + 0.15, 0, 1);
    // and the place itself is now known, whatever it turned out to hold
    this.recordPlace(b, x, z, t);
    // the people who walked to a thing no one could explain are the people
    // this will be told about: history has singled them out, and the follow
    // order will offer them first
    for (const person of b.people) {
      if (person.partyId !== party.id) continue;
      person.importance = clamp(
        person.importance + (found === 'empty' ? 0.04 : 0.12) * a.salience, 0, 1);
    }
    this.events.emit({
      type: 'omen_answered', time: t, x, z,
      radius: 200, magnitude: 1,
      salience: found === 'empty' ? 0.35 : 0.6,
      cause: { kind: 'mortal', bandId: b.id },
      data: { meaning: found },
      parentEventIds: [a.eventId],
    });
  }

  /** One person takes one memory, bounded; the faintest-held goes first. */
  private remember(person: PersonState, m: PersonMemory): void {
    person.memories.push(m);
    while (person.memories.length > MAX_PERSON_MEMORIES) {
      let lo = 0;
      for (let i = 1; i < person.memories.length; i++) {
        const a = person.memories[i], b = person.memories[lo];
        if (hold(a.importance, a.time, m.time) < hold(b.importance, b.time, m.time)) lo = i;
      }
      person.memories.splice(lo, 1);
    }
  }

  /**
   * A sign the band cannot account for becomes something it might go and
   * look at (v0.3.2 §Omen).
   *
   * The point recorded is *not* where the omen was. It is where this band
   * believes it pointed: the true place pushed somewhere inside a disc of
   * doubt whose width comes from how broadly the god cast it and how far
   * away they were standing. The offset is hashed from the band and the
   * event, so it is deterministic and so two bands that saw the same sign
   * genuinely disagree about where to go.
   *
   * Born with exactly as much credit as signs have earned in this band's
   * experience — a god whose last three portents led nowhere gets a fourth
   * that nobody bothers to walk to.
   */
  private noteAnomaly(
    b: Band,
    sign: { eventId: string; time: number; x: number; z: number;
            radius: number; salience: number },
    learnedAt: number,
  ): void {
    if (b.anomalies.some((a) => a.eventId === sign.eventId)) return;
    const dist = Math.hypot(sign.x - b.homeX, sign.z - b.homeZ);
    const unc = omenUncertainty(sign.radius, dist);
    const ang = hash01(b.idHash, sign.time, this.seed) * Math.PI * 2;
    // sqrt keeps the guess evenly spread over the disc rather than bunched
    const off = unc * Math.sqrt(hash01(sign.time, b.idHash, this.seed ^ 0x51f));
    const px = sign.x + Math.cos(ang) * off, pz = sign.z + Math.sin(ang) * off;

    /**
     * Insistence (v0.3.2 — repetition becomes history).
     *
     * A sign shown once over somewhere a day's walk away is a rumour, and
     * the first theology test proved it stays one: a single Omen 1.5 km out
     * scored 0.63 against foraging's 0.83 and nobody ever went. A god that
     * wants to be followed says it again.
     *
     * So a second reading that lands inside the doubt of a first is not a
     * competing place — it is the same place, seen twice. The two are
     * combined as independent estimates: the point moves to the
     * precision-weighted mean and the doubt genuinely *shrinks*, which is
     * what turns a rumour into somewhere worth the walk. Credibility rises
     * too, because a thing that happens twice is harder to dismiss.
     */
    const seen = b.anomalies.find((a) =>
      a.visitedAt === undefined &&
      Math.hypot(a.x - px, a.z - pz) <= a.uncertaintyRadius + unc);
    if (seen) {
      const w1 = 1 / (seen.uncertaintyRadius * seen.uncertaintyRadius);
      const w2 = 1 / (unc * unc);
      seen.x = (seen.x * w1 + px * w2) / (w1 + w2);
      seen.z = (seen.z * w1 + pz * w2) / (w1 + w2);
      seen.uncertaintyRadius = 1 / Math.sqrt(w1 + w2);
      seen.specificity = omenSpecificity(seen.uncertaintyRadius);
      seen.salience = Math.max(seen.salience, sign.salience);
      seen.credibility = clamp(seen.credibility + 0.2, 0, 1);
      seen.learnedAt = learnedAt;
      return;
    }

    b.anomalies.push({
      eventId: sign.eventId,
      x: px,
      z: pz,
      uncertaintyRadius: unc,
      specificity: omenSpecificity(unc),
      salience: sign.salience,
      learnedAt,
      credibility: clamp(b.omenTrust, 0, 1),
      meaning: 'unknown',
    });
    while (b.anomalies.length > MAX_ANOMALIES) {
      // the least worth walking to is the first let go of
      let lo = 0;
      for (let i = 1; i < b.anomalies.length; i++) {
        const x = b.anomalies[i], y = b.anomalies[lo];
        if (x.credibility * x.salience < y.credibility * y.salience) lo = i;
      }
      b.anomalies.splice(lo, 1);
    }
  }

  /**
   * The band's communal record takes an event — at witness time when it was
   * seen from the hearth, or days later when a story walks in. Strong news
   * unsettles whenever it *arrives*: a fire learned about late is still a
   * fire, and that unease is the doorway belief will walk through.
   */
  private bandRemember(
    b: Band, eventId: string, time: number, importance: number,
    x: number, z: number, grim: boolean,
  ): void {
    // a stronger telling deepens what is already known — the camp that heard
    // distant thunder learns what it really was when the witnesses walk in
    const held = b.memories.find((m) => m.eventId === eventId);
    if (held) {
      if (importance > held.importance) {
        held.importance = importance;
        held.valence = grim ? -importance : importance * 0.5;
      }
      return;
    }
    b.memories.push({
      eventId, time, importance,
      valence: grim ? -importance : importance * 0.5,
      x, z,
    });
    while (b.memories.length > MAX_MEMORIES) {
      let lo = 0;
      for (let i = 1; i < b.memories.length; i++) {
        const x = b.memories[i], y = b.memories[lo];
        if (hold(x.importance, x.time, time) < hold(y.importance, y.time, time)) lo = i;
      }
      b.memories.splice(lo, 1);
    }
    if (importance >= UNSETTLING * 0.8) {
      b.uncertainty = clamp(b.uncertainty + importance * 0.15, 0, 1);
    }
  }

  /**
   * Stories travel home with the party (v0.3.2). Once an hour, anyone at
   * the hearth with news the hearth has not heard tells it: the band's
   * record takes it at the strength the strongest witness carries, every
   * present holder is marked told, and everyone else at the hearth learns
   * it second-hand — a real memory of their own, weaker, kind 'told'. This
   * is the whole mechanism religion will later spread by: knowledge moves
   * only inside people, never across tiles.
   */
  private deliverNews(b: Band, t: number): void {
    // who is at the hearth this hour, and what news do they hold
    let news: Map<string, PersonMemory> | null = null;
    let atHome: PersonState[] | null = null;
    for (const person of b.people) {
      const party = b.parties.find((q) => q.id === person.partyId);
      const px = party?.x ?? b.x, pz = party?.z ?? b.z;
      if (Math.hypot(px - b.homeX, pz - b.homeZ) > HOME_RETURN) continue;
      (atHome ??= []).push(person);
      for (const m of person.memories) {
        if (m.told) continue;
        const held = (news ??= new Map()).get(m.eventId);
        if (!held || m.importance > held.importance) news.set(m.eventId, m);
      }
    }
    if (!news || !atHome) return;

    for (const [eventId, source] of news) {
      // the id carries its type (`${type}@…`), so a story stays grim retold
      const type = eventId.slice(0, eventId.indexOf('@'));
      this.bandRemember(b, eventId, source.time, source.importance,
                        source.x, source.z, GRIM.has(type));
      // a sign carried home becomes the community's to explain — and they
      // are working from the teller's account, so the doubt is measured
      // from the camp that heard it, not from the ground it happened on
      if (type === 'divine_omen') {
        this.noteAnomaly(b, {
          eventId, time: source.time, x: source.x, z: source.z,
          radius: source.radius ?? 0, salience: source.importance,
        }, t);
      }
      for (const person of atHome) {
        const held = person.memories.find((m) => m.eventId === eventId);
        if (held) {
          held.told = true;
        } else {
          // hearing the story is a memory of its own, dated to the telling
          this.remember(person, {
            eventId, time: t, kind: 'told',
            importance: clamp(source.importance * SECOND_HAND, 0, 1),
            x: source.x, z: source.z, told: true,
            // whose story it was is part of the story
            ...(source.from ? { from: source.from } : {}),
          });
        }
      }
    }
  }
}
